// Finance application messages (Uzbek default, Russian and English).
export const financeMessages = {
  en: {
    finance: {
      appTitle: 'Finance',
      appDescription: 'Receivables, payables, treasury, costing, and financial reports.',
      nav: {
        overview: 'Overview',
        receivables: 'Receivables',
        payables: 'Payables',
        treasury: 'Cash & bank',
        costing: 'Costing',
        productCostWorkspace: 'Product cost workspace',
        payroll: 'Payroll',
        payrollPeriods: 'Payroll periods',
        payrollAccruals: 'Labor accruals',
        payrollAdjustments: 'Adjustments',
        payrollRuns: 'Payroll runs',
        payrollPostings: 'Postings',
        payrollReports: 'Reports',
        payrollStatement: 'Employee statement',
        labourCost: 'Labour cost',
        invoices: 'Invoices',
        collections: 'Collections',
        revenueVsCollections: 'Revenue vs collections',
        adjustments: 'Adjustments',
        supplierInvoices: 'Supplier invoices',
        supplierPayments: 'Supplier payments',
        payablesAging: 'Payables aging',
        accounts: 'Accounts',
        transfers: 'Transfers',
        cashFlow: 'Cash flow',
        currencyExposure: 'Currency exposure',
        reconciliation: 'Reconciliation',
      },
      common: {
        actions: 'Actions',
        open: 'Open',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        create: 'Create',
        edit: 'Edit',
        delete: 'Delete',
        retry: 'Retry',
        refresh: 'Refresh',
        loading: 'Loading…',
        empty: 'Nothing to show.',
        search: 'Search',
        searchPlaceholder: 'Search by name, code or reference…',
        apply: 'Apply',
        reset: 'Reset',
        clear: 'Clear',
        all: 'All',
        allStatuses: 'All statuses',
        allTypes: 'All types',
        allCurrencies: 'All currencies',
        allMethods: 'All methods',
        allAccounts: 'All accounts',
        none: 'None',
        optional: 'optional',
        yes: 'Yes',
        no: 'No',
        active: 'Active',
        inactive: 'Inactive',
        status: 'Status',
        type: 'Type',
        date: 'Date',
        dueDate: 'Due date',
        dateFrom: 'From',
        dateTo: 'To',
        asOf: 'As of',
        amount: 'Amount',
        currency: 'Currency',
        exchangeRate: 'Exchange rate',
        total: 'Total',
        balance: 'Balance',
        openingBalance: 'Opening balance',
        currentBalance: 'Current balance',
        reference: 'Reference',
        notes: 'Notes',
        reason: 'Reason',
        agent: 'Counterparty',
        account: 'Account',
        method: 'Method',
        direction: 'Direction',
        invoice: 'Invoice',
        invoices: 'Invoices',
        payments: 'Payments',
        rows: '{count} rows',
        page: 'Page {page} of {total}',
        previous: 'Previous',
        next: 'Next',
        back: 'Back',
        close: 'Close',
        noPermission: 'You do not have permission for this action.',
        invalidRange: 'The start date must not be after the end date.',
        requiredField: '{field} is required.',
        saved: 'Saved.',
        deleted: 'Deleted.',
        detail: 'Details',
        allDone: 'All done',
        showing: 'Showing {from}–{to} of {total}',
      },
      paymentMethod: {
        cash: 'Cash',
        card: 'Card',
        transfer: 'Bank transfer',
      },
      paymentDirection: {
        inflow: 'Inflow',
        outflow: 'Outflow',
      },
      invoiceStatus: {
        draft: 'Draft',
        unpaid: 'Unpaid',
        partial: 'Partially paid',
        paid: 'Paid',
      },
      supplierInvoiceStatus: {
        open: 'Open',
        partial: 'Partial',
        closed: 'Closed',
        void: 'Void',
        overdue: 'Overdue',
      },
      accountType: {
        cashbox: 'Cashbox',
        bank: 'Bank',
        card: 'Card',
        clearing: 'Clearing',
      },
      adjustmentType: {
        customer_credit: 'Customer credit',
        write_off: 'Write-off',
        debit_note: 'Debit note',
        refund: 'Refund',
      },
      adjustmentStatus: {
        draft: 'Draft',
        posted: 'Posted',
        reversed: 'Reversed',
      },
      transferStatus: {
        posted: 'Posted',
        reversed: 'Reversed',
      },
      entryKind: {
        payment: 'Payment',
        payment_reversal: 'Payment reversal',
        transfer: 'Transfer',
        transfer_reversal: 'Transfer reversal',
        transfer_fee: 'Transfer fee',
        transfer_fee_reversal: 'Transfer fee reversal',
        adjustment: 'Adjustment',
      },
      agingBucket: {
        current: 'Not due',
        days1_30: '1–30 days',
        days31_60: '31–60 days',
        days60Plus: '60+ days',
      },
      groupBy: {
        day: 'Daily',
        week: 'Weekly',
        month: 'Monthly',
      },
      overview: {
        title: 'Finance overview',
        cashPosition: 'Cash & bank',
        cashAccounts: 'Accounts',
        receivables: 'Receivables',
        payables: 'Payables',
        collected: 'Collected',
        invoiced: 'Invoiced',
        collectionGap: 'Collection gap',
        collectionRate: 'Collection rate',
        netCashFlow: 'Net cash flow',
        cashIn: 'Cash in',
        cashOut: 'Cash out',
        overdue: 'Overdue payables',
        asOf: 'as of {date}',
        thisMonth: 'This month',
        unreconciled: 'Reconciliation differences found',
        reconciled: 'Ledger is reconciled',
        quickLinks: 'Go to',
        quickReceivables: 'Customer balances',
        quickReceivablesHint: 'Open balances and collections',
        quickPayables: 'Supplier invoices',
        quickPayablesHint: 'Register and settle purchase invoices',
        quickAccounts: 'Accounts',
        quickAccountsHint: 'Cashboxes, banks and their ledgers',
        quickCosting: 'Product costing',
        quickCostingHint: 'Standard cost snapshots per product',
        recentPayments: 'Latest payments',
        noPayments: 'No payments recorded yet.',
        warnings: 'Warnings',
        loadError: 'The finance overview could not be loaded.',
      },
      accounts: {
        title: 'Financial accounts',
        newAccount: 'New account',
        editAccount: 'Edit account',
        accountCode: 'Account code',
        accountName: 'Name',
        accountType: 'Account type',
        department: 'Department',
        branch: 'Branch',
        transactions: 'Transactions',
        transactionsFor: 'Transactions — {name}',
        ledgerTitle: 'Account ledger',
        entryKind: 'Entry type',
        postedAt: 'Posted at',
        noTransactions: 'No transactions for this account yet.',
        netByCurrency: 'Net position by currency',
        activeAccount: 'Active',
        accountSaved: 'Account saved.',
        loadError: 'Accounts could not be loaded.',
        empty: 'No accounts yet. Create a cashbox or bank account to get started.',
        deleteHint: 'Accounts cannot be deleted; deactivate them instead.',
      },
      transfers: {
        title: 'Financial transfers',
        newTransfer: 'New transfer',
        sourceAccount: 'From account',
        destinationAccount: 'To account',
        sourceAmount: 'Source amount',
        destinationAmount: 'Destination amount',
        feeAmount: 'Fee',
        feeAccount: 'Fee account',
        transferDate: 'Transfer date',
        reversed: 'Transfer reversed.',
        reverse: 'Reverse',
        reverseConfirm: 'Reverse this transfer? The linked treasury entries are reversed as well.',
        empty: 'No transfers yet.',
        loadError: 'Transfers could not be loaded.',
        sameAccount: 'Choose two different accounts.',
        detailTitle: 'Transfer #{id}',
      },
      picker: {
        accountTitle: 'Select an account',
        accountSearch: 'Search accounts by name or code…',
        invoiceTitle: 'Select an invoice',
        invoiceSearch: 'Search invoices by number or counterparty…',
        agentTitle: 'Select a counterparty',
        agentEyebrow: 'Counterparties',
        agentSearch: 'Counterparty',
        agentPlaceholder: 'Search by name or phone…',
        agentEmpty: 'No counterparties found.',
        agentLoading: 'Loading counterparties…',
        productTitle: 'Select a product',
        productEyebrow: 'Products',
        productSearch: 'Product',
        productPlaceholder: 'Search by name or SKU…',
        productEmpty: 'No products found.',
        productLoading: 'Loading products…',
        expenseTitle: 'Select an overhead expense',
        expenseEyebrow: 'Overhead catalogue',
        expenseSearch: 'Overhead expense',
        expensePlaceholder: 'Search by name or code…',
        expenseEmpty: 'No overhead expenses found.',
        expenseLoading: 'Loading overhead expenses…',
        roleCustomer: 'Customer',
        roleSupplier: 'Supplier',
        page: 'Page {page} of {total}',
        pageRange: '{from}–{to} of {total}',
        close: 'Close',
        retry: 'Retry',
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
      },
      reports: {
        period: 'Period',
        groupBy: 'Group by',
        cashIn: 'Cash in',
        cashOut: 'Cash out',
        net: 'Net',
        netTotal: 'Net cash flow',
        receivablesAmount: 'Receivables',
        payablesAmount: 'Payables',
        cashAmount: 'Cash',
        customerAdvances: 'Customer advances',
        netExposure: 'Net exposure',
        fxRate: 'FX rate',
        baseCurrency: 'Base currency',
        currenciesCount: 'Currencies',
        warnings: 'Warnings',
        reconciled: 'Reconciled',
        notReconciled: 'Differences',
        paymentPostingDelta: 'Posting delta',
        receivablesBalance: 'Receivables balance',
        payablesBalance: 'Payables balance',
        treasuryBalance: 'Treasury balance',
        activeAccountedPayments: 'Accounted payments',
        activePaymentPostings: 'Payment postings',
        empty: 'No data for the selected period.',
        loadError: 'The report could not be loaded.',
        invoiced: 'Invoiced',
        collected: 'Collected',
        gap: 'Gap',
        rate: 'Collection rate',
        allocatedCollections: 'Allocated collections',
        unallocatedCollections: 'Unallocated collections',
        refunds: 'Refunds',
        creditNotes: 'Credit notes',
        writeOffs: 'Write-offs',
        debitNotes: 'Debit notes',
      },
      receivables: {
        title: 'Invoices',
        detailTitle: 'Invoice #{id}',
        order: 'Order',
        shipment: 'Shipment',
        amount: 'Amount',
        paid: 'Paid',
        outstanding: 'Outstanding',
        created: 'Created',
        lines: 'Invoice lines',
        allocations: 'Payment allocations',
        product: 'Product',
        sku: 'SKU',
        quantity: 'Quantity',
        unitPrice: 'Unit price',
        lineTotal: 'Line total',
        allocationDate: 'Allocation date',
        allocatedAmount: 'Allocated',
        noAllocations: 'No payment allocations recorded for this invoice yet.',
        empty: 'No invoices found.',
        loadError: 'Invoices could not be loaded.',
      },
      payments: {
        newPayment: 'Record payment',
        editPayment: 'Edit payment',
        reverse: 'Reverse',
        reverseConfirm: 'Reverse this payment? Its treasury posting and invoice allocations are reversed and the record is removed.',
        reversed: 'Payment reversed.',
        empty: 'No payments found.',
        allocationIncomplete: 'Every allocation needs an invoice and an amount greater than zero.',
        allocationExceeds: 'The allocated total exceeds the payment amount.',
        addAllocation: 'Add invoice',
        allocatedTotal: 'Allocated',
        unallocated: 'Unallocated',
        selectCounterparty: 'Select a counterparty to load its open invoices.',
        noOpenInvoices: 'No open invoices for the selected counterparty.',
        selectAllocationInvoice: 'Select invoice',
        recordForInvoice: 'Record payment',
        advanceNotice: 'A payment without allocations is kept as a customer advance.',
        loadError: 'Payments could not be loaded.',
      },
      adjustments: {
        title: 'Invoice adjustments',
        newAdjustment: 'New adjustment',
        adjustmentNumber: 'Adjustment number',
        numberHint: 'Required and must be unique.',
        reasonHint: 'Adjustments correct an issued invoice without a payment.',
        refundAccountRequired: 'A refund requires a financial account in the invoice currency.',
        refundAccountHint: 'Refunds post to this account.',
        statusHint: 'Posted adjustments change the invoice balance immediately.',
        selectInvoice: 'Select invoice',
        noInvoices: 'No adjustable invoices for this counterparty.',
        post: 'Post',
        postConfirm: 'Post this adjustment? The invoice balance and the account posting are updated.',
        posted: 'Adjustment posted.',
        reverse: 'Reverse',
        reverseConfirm: 'Reverse this adjustment? The invoice balance and the treasury posting are restored.',
        reversed: 'Adjustment reversed.',
        empty: 'No adjustments found.',
        loadError: 'Adjustments could not be loaded.',
      },
      payables: {
        supplierInvoicesTitle: 'Supplier invoices',
        newInvoice: 'New supplier invoice',
        editInvoice: 'Edit supplier invoice',
        supplier: 'Supplier',
        invoiceNumber: 'Invoice number',
        invoiceDate: 'Invoice date',
        numberHint: 'Must be unique per supplier.',
        purchaseOrder: 'Purchase order',
        noPurchaseOrders: 'No matching purchase orders for this supplier and currency.',
        purchaseOrdersUnavailable: 'Purchase orders are unavailable without the purchase-orders permission.',
        department: 'Department',
        warehouse: 'Warehouse',
        openTotal: 'Open total',
        statusHint: 'Settlement status follows the payment allocations; only void can be set directly.',
        dueBeforeInvoice: 'The due date cannot be before the invoice date.',
        void: 'Void',
        voidConfirm: 'Void this supplier invoice? It stays in the register but counts as cancelled.',
        voided: 'Supplier invoice voided.',
        deleteConfirm: 'Delete this supplier invoice? The record is removed from the register.',
        noAllocations: 'No payment allocations recorded for this invoice yet.',
        empty: 'No supplier invoices found.',
        loadError: 'Supplier invoices could not be loaded.',
        agingTitle: 'Payables aging',
        totalPayables: 'Total payables',
        overdueDays: 'Overdue days',
        bucket: 'Aging bucket',
        agingEmpty: 'No outstanding payables for the selected filters.',
      },
      costing: {
        title: 'Product costing',
        selectProduct: 'Select a product',
        productPrompt: 'Choose a product to load its cost calculations.',
        productionType: 'Production type',
        productionTypePlaceholder: 'Filter by production type…',
        version: 'Formula version',
        calculatedBy: 'Calculated by',
        calculatedAt: 'Calculated at',
        baseCost: 'Base cost',
        wasteCost: 'Waste',
        costBeforeProfit: 'Cost before profit',
        profitCost: 'Profit',
        finalCost: 'Final cost',
        standardCost: 'Latest cost (standard)',
        variance: 'Change vs previous',
        noVariance: 'No earlier calculation',
        components: 'Cost components',
        component: 'Component',
        componentType: 'Type',
        group: 'Group',
        amount: 'Amount',
        quantityPerUnit: 'Qty per unit',
        sourceUnitCost: 'Source unit cost',
        noComponents: 'This calculation has no components.',
        marginAnalysis: 'Margin analysis',
        pricelist: 'Pricelist',
        noPricelist: 'No pricelist available',
        price: 'Selling price',
        noPrice: 'This product has no price in the selected pricelist.',
        margin: 'Margin',
        marginRate: 'Margin rate',
        currencyMismatch: 'The price currency differs from the cost currency, so the margin is not comparable.',
        empty: 'No cost calculations for this product yet.',
        loadError: 'Cost calculations could not be loaded.',
      },
      productCost: {
        title: 'Product cost',
        subtitle: 'Versions, sources and approval',
        backToCosting: 'Historical calculations',
        refreshList: 'Refresh list',
        allProducts: 'All products',
        find: 'Search',
        newDraft: 'New draft',
        versions: 'Versions',
        noVersions: 'No versions yet',
        versionHeading: '{product} · version {version}',
        batch: 'Batch',
        expectedOutput: 'Expected output',
        yield: 'Yield',
        checkSources: 'Check sources',
        refreshVersion: 'Refresh version',
        edit: 'Edit',
        clone: 'Create copy',
        pricingLegend: 'Proposed price on the next calculation',
        pricingMethod: 'Method',
        pricing: {
          markup: 'Markup % on cost',
          targetMargin: 'Target margin % of price',
          manual: 'Manual price',
          markupShort: 'Markup',
          marginShort: 'Margin',
          manualShort: 'Manual price',
        },
        pricingNote: 'Markup and margin are not included in the production cost.',
        sourceQuality: 'Source quality',
        calculable: 'Calculation possible',
        noIssues: 'Sources verified, no issues found.',
        blocking: 'Blocks calculation',
        warning: 'Warning',
        details: 'Details',
        bomComposition: 'BOM composition',
        bomSummary: '{materials} material(s) · {operations} operation(s)',
        bomNote: 'All BOM lines are shown regardless of the calculation result. The purchase price takes precedence over the operator price.',
        material: 'Material',
        qtyPerBatch: 'Qty per batch',
        unit: 'Unit',
        sourcePrice: 'Source price',
        priceSource: 'Price source',
        operatorPrice: 'Operator price',
        operatorRate: 'Operator rate',
        materialMeta: '{sku} · operation {sequence}',
        operation: 'Operation',
        durationMinutes: 'Duration, min',
        rate: 'Rate',
        rateSource: 'Rate source',
        noBomMaterials: 'The BOM has no materials.',
        noBomOperations: 'The BOM has no operations.',
        operatorPricesNote: 'Changed operator prices and rates are sent with the source check and the calculation.',
        priceMissing: 'not found — enter the operator price',
        pricePurchaseFromOrders: 'purchase (from orders)',
        pricePurchase: 'purchase price',
        priceOperator: 'operator price',
        purchaseInUse: 'purchase price is used',
        enterPrice: 'enter a price',
        enterRate: 'enter a rate',
        rateMissing: 'not found',
        elementKnitting: 'Knitting materials',
        elementOtherMaterial: 'Other materials',
        elementPackaging: 'Packaging materials',
        elementLabour: 'Direct labour',
        elementMachine: 'Machine',
        elementSubcontracting: 'Subcontracting',
        elementVariableOverhead: 'Variable overhead',
        elementFixedOverhead: 'Fixed overhead',
        elementQuality: 'Quality and scrap',
        elementOtherCost: 'Other direct cost',
        priceOverrideIgnored: 'Purchase price {price} {currency} is used; the operator price is ignored.',
        priceAppliedDifferent: 'The API applied {applied} instead of the {typed} that was entered.',
        serverGroupingNote: 'Material totals are grouped by the API cost element; the tabs group material lines by their BOM operation.',
        tabsLabel: 'Costing sections',
        tab: {
          knitting: 'Knitting materials',
          materials: 'Other materials',
          operations: 'Operations',
          expenses: 'Expenses',
          warnings: 'Warnings',
          summary: 'Summary',
          audit: 'Audit',
        },
        lines: {
          item: 'Item / source',
          quantity: 'Quantity',
          sourceRate: 'Source rate',
          currencyId: 'Currency ID',
          fx: 'FX',
          costRate: 'Rate in {currency}',
          amount: 'Amount',
          origin: 'Origin',
        },
        noLines: 'No calculated lines in this section.',
        overridesGap: 'Manual overrides are not supported by the API yet. Fix the source data and recalculate.',
        serverTotals: 'Server totals · {currency}',
        totals: {
          item: 'Item',
          perBatch: 'Per batch',
          perUnit: 'Per unit',
        },
        proposedPrices: 'Proposed prices',
        prices: {
          method: 'Method',
          value: 'Value',
          unitPrice: 'Unit price',
        },
        publishGap: 'Publishing to the pricelist is unavailable until the corresponding API is connected.',
        comparableVersion: 'Comparable version',
        selectVersion: 'Select a version',
        compare: 'Compare',
        comparisonHint: 'Comparable versions from the last 200 calculations of the product are shown.',
        unitCostDelta: 'Unit cost change (server)',
        comparison: {
          item: 'Item',
          quantityLeft: 'Qty left',
          quantityRight: 'Qty right',
          amountLeft: 'Amount left',
          amountRight: 'Amount right',
        },
        added: 'Added',
        removed: 'Removed',
        approvalData: 'Approval data',
        approvalGap: 'Version fields are shown. Full transition history requires a separate audit API.',
        auditFields: {
          createdByUserId: 'Created by (user ID)',
          createdAt: 'Created at',
          calculatedByUserId: 'Calculated by (user ID)',
          calculatedAt: 'Calculated at',
          submittedByUserId: 'Submitted by (user ID)',
          submittedAt: 'Submitted at',
          approvedByUserId: 'Approved by (user ID)',
          approvedAt: 'Approved at',
          publishedByUserId: 'Published by (user ID)',
          publishedAt: 'Published at',
          rowVersion: 'Row version',
          calculationChecksum: 'Calculation checksum',
        },
        selectVersionPrompt: 'Select a version or create a new draft.',
        publishConfirm: 'Publish this version? The previous standard may be superseded.',
        calculateConfirm: 'Recalculate and replace the saved cost lines?',
        invalidPricing: 'Enter a non-negative value; the target margin must be below 100%.',
        incompatibleVersions: 'The versions have different parameters or currencies. Refresh the data.',
        status: {
          draft: 'Draft',
          calculated: 'Calculated',
          pending_approval: 'Pending approval',
          approved: 'Approved',
          published: 'Published',
          rejected: 'Rejected',
        },
        guidance: {
          bom_missing: 'Create a BOM for the product or select another version.',
          material_uom_missing: 'Set up a single active base unit of measure for the material.',
          material_price_missing: 'Enter a purchase price valid on the costing date.',
          material_price_stale: 'Check whether the purchase price is current and update it if needed.',
          fx_rate_missing: 'Add the approved accounting rate for the costing date.',
          operation_rate_missing: 'Enter the operation rate for the selected shift.',
          overhead_rule_conflict: 'Resolve the overlap between approved overhead allocation rules.',
        },
        guidanceFallback: 'Check the source data before calculating.',
        action: {
          calculate: 'Calculate',
          submit: 'Submit for approval',
          approve: 'Approve',
          reject: 'Reject',
          publish: 'Publish',
        },
        warningsPrompt: 'Run the source check to collect the current warnings.',
        overheads: {
          title: 'Overhead expenses',
          help: 'Approved rules are applied on the next source check or calculation. Draft rules are stored but ignored.',
          add: 'Add overhead expense',
          expense: 'Overhead expense',
          selectExpense: 'Select an overhead expense',
          empty: 'No overhead rules were found.',
          noneApplicable: 'No overhead rule applies to this version.',
          noPermission: 'Viewing overhead rules requires the cost_overheads.view permission.',
          loading: 'Loading overhead rules…',
          code: 'Code',
          name: 'Name',
          basis: 'Allocation basis',
          rateType: 'Rate type',
          rateValue: 'Rate',
          percent: 'Percent',
          amount: 'Amount',
          currency: 'Currency',
          scope: 'Scope',
          scopeProduct: 'This product',
          scopeAll: 'All products',
          scopeGroup: 'Product group',
          scopeDepartment: 'Department',
          effectiveFrom: 'Valid from',
          effectiveTo: 'Valid to',
          priority: 'Priority',
          approveNow: 'Approve immediately',
          save: 'Save overhead',
          cancel: 'Cancel',
          approve: 'Approve',
          statusDraft: 'Draft',
          statusApproved: 'Approved',
          statusRetired: 'Retired',
          basisDirectMaterialPercent: 'Direct material %',
          basisDirectLaborPercent: 'Direct labour %',
          basisPrimeCostPercent: 'Prime cost %',
          basisPerProducedUnit: 'Per produced unit',
          basisPerMachineHour: 'Per machine hour',
          basisPerLaborHour: 'Per labour hour',
          basisFixedPerBatch: 'Fixed per batch',
          invalid: 'Code, name, basis, a positive rate and the start date are required.',
          currencyRequired: 'Select the currency of the amount rule.',
        },
        editor: {
          titleCreate: 'New version',
          titleEdit: 'Edit draft',
          titleClone: 'Version copy',
          cloneNote: 'A new draft of source assumptions is created. Calculate it to get current sources and amounts.',
          permissions: 'Selecting references requires products.view and bom.view permissions.',
          retryLoad: 'Retry loading',
          product: 'Product',
          selectProduct: 'Select a product',
          bomVersion: 'BOM version',
          selectBom: 'Select a BOM',
          active: 'active',
          inactive: 'inactive',
          currency: 'Currency',
          selectCurrency: 'Select a currency',
          scenarioName: 'Scenario name',
          productionType: 'Production type',
          costingDate: 'Costing date',
          effectiveFrom: 'Valid from',
          effectiveTo: 'Valid to',
          batchQuantity: 'Batch size',
          expectedOutputQuantity: 'Expected output',
          yieldRate: 'Yield rate',
          costType: 'Type',
          costTypeStandard: 'Standard',
          costTypePlanned: 'Planned',
          shift: 'Shift',
          shiftDay: 'Day',
          shiftNight: 'Night',
          shiftBlended: 'Blended',
          notes: 'Note',
          save: 'Save',
          cancel: 'Cancel',
          noBoms: 'No BOM versions found for the product.',
          productBomRequired: 'Select a product and its BOM version.',
          currencyRequired: 'Select a currency.',
          positiveNumbers: 'Quantity and yield must be positive numbers with up to 8 decimal places.',
          yieldMax: 'The yield rate must not exceed 1.',
          periodInvalid: 'Check the validity period.',
        },
      },
      payroll: {
        title: 'Payroll labour cost',
        gapTitle: 'Payroll runs are generated from production and adjustments',
        gapNotice: 'The payroll API covers periods, labour accruals, adjustments, runs and postings — use the Payroll pages for those. This view stays as the raw timesheet labour-cost report for the same period.',
        labourCost: 'Labour cost',
        hours: 'Hours',
        cycles: 'Cycles',
        employees: 'Employees',
        entries: 'Entries',
        employee: 'Employee',
        period: 'Period',
        total: 'Total',
        empty: 'No timesheet entries for the selected period.',
        loadError: 'The labour-cost report could not be loaded.',
      },
      payrollFlow: {
        allPeriods: 'All periods',
        selectPeriod: 'Select a period',
        approve: 'Approve',
        reject: 'Reject',
        reverse: 'Reverse',
        rejectTitle: 'Reject item',
        reverseTitle: 'Reverse item',
        rejectMessage: 'The reason is stored with the rejected row.',
        reverseMessage: 'The reversal reason is kept in the audit trail.',
        statusOpen: 'Open',
        statusLocked: 'Locked',
        statusClosed: 'Closed',
        employeePickerTitle: 'Select an employee',
        employeePickerEyebrow: 'Payroll',
        employeePickerSearch: 'Employee',
        employeePickerPlaceholder: 'Search by name or employee code…',
        employeePickerEmpty: 'No production employees found.',
        periods: {
          eyebrow: 'Payroll',
          title: 'Payroll periods',
          description: 'Periods define the payroll window. Only open periods can be edited; locking keeps accruals possible but freezes the dates.',
          newPeriod: 'New period',
          editPeriod: 'Edit period',
          formHint: 'Periods cannot overlap and the end date must not be before the start date.',
          code: 'Code',
          name: 'Name',
          range: 'Period',
          lockedAt: 'Locked at',
          lock: 'Lock',
          close: 'Close',
          lockConfirm: 'Lock {code}? Accruals can still be added to it.',
          closeConfirm: 'Close {code}? A closed period is final.',
          created: 'Period created.',
          updated: 'Period updated.',
          locked: 'Period locked.',
          closed: 'Period closed.',
          empty: 'No payroll periods yet.',
        },
        accruals: {
          eyebrow: 'Payroll',
          title: 'Labor accruals',
          description: 'Production earnings created when a worker session finishes with output. Approving makes them payable in a payroll run.',
          employee: 'Employee',
          production: 'Production',
          completed: 'Completed',
          accepted: 'Accepted',
          rejected: 'Rejected',
          rework: 'Rework',
          rate: 'Rate',
          gross: 'Gross',
          doneApproved: 'Accrual for {employee} approved.',
          doneRejected: 'Accrual rejected.',
          doneReversed: 'Accrual reversed.',
          empty: 'No accruals for the selected filters.',
        },
        accrualStatusProvisional: 'Provisional',
        accrualStatusApproved: 'Approved',
        accrualStatusRejected: 'Rejected',
        accrualStatusExported: 'Exported',
        accrualStatusPaid: 'Paid',
        accrualStatusReversed: 'Reversed',
        adjustments: {
          eyebrow: 'Payroll',
          title: 'Payroll adjustments',
          description: 'Manual bonus, deduction, advance, penalty and correction items. Bonus and correction become earnings; the others become deductions.',
          create: 'New adjustment',
          employee: 'Employee',
          selectEmployee: 'Select an employee for the adjustment.',
          period: 'Period',
          periodRequired: 'Select a payroll period.',
          amountRequired: 'Enter an amount greater than zero.',
          currencyRequired: 'Select a currency.',
          typeHint: 'Bonus and correction increase gross pay; deduction, advance and penalty reduce net pay.',
          created: 'Adjustment created.',
          doneApproved: 'Adjustment approved.',
          doneRejected: 'Adjustment rejected.',
          doneReversed: 'Adjustment reversed.',
          empty: 'No adjustments for the selected filters.',
        },
        adjustmentTypeBonus: 'Bonus',
        adjustmentTypeDeduction: 'Deduction',
        adjustmentTypeAdvance: 'Advance',
        adjustmentTypePenalty: 'Penalty',
        adjustmentTypeCorrection: 'Correction',
        adjustmentStatusDraft: 'Draft',
        adjustmentStatusApproved: 'Approved',
        adjustmentStatusRejected: 'Rejected',
        adjustmentStatusUsed: 'Used',
        adjustmentStatusReversed: 'Reversed',
        runs: {
          eyebrow: 'Payroll',
          title: 'Payroll runs',
          description: 'One run per period and currency. A run collects approved accruals and adjustments into employee lines.',
          generate: 'Generate run',
          generateHint: 'Different currencies are never merged — generate one run per currency.',
          period: 'Period',
          periodRequired: 'Select a payroll period.',
          currencyRequired: 'Select a currency.',
          code: 'Code',
          codeHint: 'Optional — the backend builds PAYRUN-{period}-{currency} when this is empty.',
          range: 'Period',
          gross: 'Gross',
          deductions: 'Deductions',
          net: 'Net',
          lines: 'Lines',
          empty: 'No payroll runs yet.',
          generated: 'Payroll run {code} generated.',
          generatedPlain: 'Payroll run generated.',
          recalculate: 'Recalculate',
          approve: 'Approve run',
          post: 'Post run',
          recalculated: 'Run recalculated.',
          approvedRun: 'Run approved.',
          posted: 'Run posted to the financial account.',
          accountRequired: 'Select a financial account.',
          postConfirm: 'Post run {code}? This creates a treasury outflow per employee line.',
          account: 'Financial account',
          accountHint: 'Only approved runs can be posted and the account currency must match the run currency.',
          accountCurrencyMismatch: 'This account is not in {currency}.',
          detailTitle: 'Payroll run',
          lineTitle: 'Employee lines',
          noLines: 'This run has no employee lines yet.',
          employee: 'Employee',
          department: 'Department',
          earnings: 'Earnings',
          deductionLines: 'Deductions',
          quantity: 'Quantity',
          amount: 'Amount',
        },
        runStatusDraft: 'Draft',
        runStatusApproved: 'Approved',
        runStatusPosted: 'Posted',
        runStatusPaid: 'Paid',
        runStatusReversed: 'Reversed',
        postings: {
          eyebrow: 'Payroll',
          title: 'Payroll postings',
          description: 'Posted net pay per employee and the treasury transaction behind it. Reversing books the money back.',
          employee: 'Employee',
          run: 'Run',
          account: 'Account',
          net: 'Net',
          postedAt: 'Posted at',
          doneReversed: 'Posting for {employee} reversed.',
          empty: 'No payroll postings yet.',
        },
        postingStatusPosted: 'Posted',
        postingStatusPaid: 'Paid',
        postingStatusReversed: 'Reversed',
        reports: {
          eyebrow: 'Payroll',
          title: 'Payroll reports',
          description: 'Read-only totals and reconciliations for the selected period, currency and date window.',
          runNet: 'Run net total',
          runGross: 'Run gross',
          runDeductions: 'Run deductions',
          runCount: 'Runs',
          employeeCount: 'Employees',
          section: 'Section',
          rows: 'Rows',
          gross: 'Gross',
          settled: 'Settled',
          open: 'Open',
          runs: 'Runs',
          accruals: 'Accruals',
          postings: 'Postings',
          empty: 'No payroll data for the selected filters.',
          accrualReconciliation: 'Accrual reconciliation',
          accrualReconciliationHint: 'Accrual totals compared with the payroll earning lines they produced.',
          postingReconciliation: 'Posting reconciliation',
          postingReconciliationHint: 'Run net totals compared with active payroll postings.',
          reconciled: 'Reconciled — no difference.',
          delta: 'Difference of {amount} needs review.',
          noRows: 'Nothing to reconcile for these filters.',
          accrualGross: 'Accrual gross',
          payrollEarnings: 'Run earnings',
          unexportedApproved: 'Approved, not in a run',
          activePostings: 'Active postings',
          reversedPostings: 'Reversed postings',
          deltaColumn: 'Difference',
        },
        statement: {
          eyebrow: 'Payroll',
          title: 'Employee statement',
          description: 'Earnings, deductions and postings for one employee. The employee is required — the report rejects an empty employee filter.',
          selectEmployee: 'Select an employee to see their statement.',
          noEarnings: 'No earnings for this employee in the selected window.',
          noDeductions: 'No deductions for this employee in the selected window.',
          noPostings: 'No postings for this employee in the selected window.',
        },
      },
    },
  },
  ru: {
    finance: {
      appTitle: 'Финансы',
      appDescription: 'Дебиторка, кредиторка, касса и банк, себестоимость и финансовые отчёты.',
      nav: {
        overview: 'Обзор',
        receivables: 'Дебиторская задолженность',
        payables: 'Кредиторская задолженность',
        treasury: 'Касса и банк',
        costing: 'Себестоимость',
        productCostWorkspace: 'Калькуляция продукции',
        payroll: 'Зарплата',
        payrollPeriods: 'Периоды',
        payrollAccruals: 'Начисления за труд',
        payrollAdjustments: 'Корректировки',
        payrollRuns: 'Расчётные ведомости',
        payrollPostings: 'Проводки',
        payrollReports: 'Отчёты',
        payrollStatement: 'Расчётный лист',
        labourCost: 'Затраты на труд',
        invoices: 'Счета',
        collections: 'Поступления',
        revenueVsCollections: 'Выручка и поступления',
        adjustments: 'Корректировки',
        supplierInvoices: 'Счета поставщиков',
        supplierPayments: 'Оплаты поставщикам',
        payablesAging: 'Просрочка к оплате',
        accounts: 'Счета учёта',
        transfers: 'Переводы',
        cashFlow: 'Движение денег',
        currencyExposure: 'Валютная позиция',
        reconciliation: 'Сверка',
      },
      common: {
        actions: 'Действия',
        open: 'Открыть',
        cancel: 'Отмена',
        save: 'Сохранить',
        saving: 'Сохранение…',
        create: 'Создать',
        edit: 'Изменить',
        delete: 'Удалить',
        retry: 'Повторить',
        refresh: 'Обновить',
        loading: 'Загрузка…',
        empty: 'Нет данных.',
        search: 'Поиск',
        searchPlaceholder: 'Поиск по названию, коду или референсу…',
        apply: 'Применить',
        reset: 'Сбросить',
        clear: 'Очистить',
        all: 'Все',
        allStatuses: 'Все статусы',
        allTypes: 'Все типы',
        allCurrencies: 'Все валюты',
        allMethods: 'Все способы',
        allAccounts: 'Все счета',
        none: 'Нет',
        optional: 'необязательно',
        yes: 'Да',
        no: 'Нет',
        active: 'Активен',
        inactive: 'Неактивен',
        status: 'Статус',
        type: 'Тип',
        date: 'Дата',
        dueDate: 'Срок оплаты',
        dateFrom: 'С',
        dateTo: 'По',
        asOf: 'На дату',
        amount: 'Сумма',
        currency: 'Валюта',
        exchangeRate: 'Курс',
        total: 'Итого',
        balance: 'Баланс',
        openingBalance: 'Начальный остаток',
        currentBalance: 'Текущий баланс',
        reference: 'Референс',
        notes: 'Примечания',
        reason: 'Основание',
        agent: 'Контрагент',
        account: 'Счёт',
        method: 'Способ',
        direction: 'Направление',
        invoice: 'Счёт',
        invoices: 'Счета',
        payments: 'Платежи',
        rows: '{count} строк',
        page: 'Стр. {page} из {total}',
        previous: 'Назад',
        next: 'Вперёд',
        back: 'Назад',
        close: 'Закрыть',
        noPermission: 'Недостаточно прав для этого действия.',
        invalidRange: 'Дата начала не может быть позже даты окончания.',
        requiredField: 'Поле «{field}» обязательно.',
        saved: 'Сохранено.',
        deleted: 'Удалено.',
        detail: 'Детали',
        allDone: 'Завершено',
        showing: 'Показано {from}–{to} из {total}',
      },
      paymentMethod: {
        cash: 'Наличные',
        card: 'Карта',
        transfer: 'Безналичный',
      },
      paymentDirection: {
        inflow: 'Поступление',
        outflow: 'Списание',
      },
      invoiceStatus: {
        draft: 'Черновик',
        unpaid: 'Не оплачен',
        partial: 'Частично оплачен',
        paid: 'Оплачен',
      },
      supplierInvoiceStatus: {
        open: 'Открыт',
        partial: 'Частично',
        closed: 'Закрыт',
        void: 'Аннулирован',
        overdue: 'Просрочен',
      },
      accountType: {
        cashbox: 'Касса',
        bank: 'Банк',
        card: 'Карта',
        clearing: 'Клиринг',
      },
      adjustmentType: {
        customer_credit: 'Кредит клиенту',
        write_off: 'Списание',
        debit_note: 'Дебет-нота',
        refund: 'Возврат',
      },
      adjustmentStatus: {
        draft: 'Черновик',
        posted: 'Проведена',
        reversed: 'Сторнирована',
      },
      transferStatus: {
        posted: 'Проведён',
        reversed: 'Сторнирован',
      },
      entryKind: {
        payment: 'Платёж',
        payment_reversal: 'Сторно платежа',
        transfer: 'Перевод',
        transfer_reversal: 'Сторно перевода',
        transfer_fee: 'Комиссия перевода',
        transfer_fee_reversal: 'Сторно комиссии',
        adjustment: 'Корректировка',
      },
      agingBucket: {
        current: 'Не просрочено',
        days1_30: '1–30 дней',
        days31_60: '31–60 дней',
        days60Plus: '60+ дней',
      },
      groupBy: {
        day: 'По дням',
        week: 'По неделям',
        month: 'По месяцам',
      },
      overview: {
        title: 'Обзор финансов',
        cashPosition: 'Касса и банк',
        cashAccounts: 'Счета',
        receivables: 'Дебиторская задолженность',
        payables: 'Кредиторская задолженность',
        collected: 'Поступило',
        invoiced: 'Выставлено',
        collectionGap: 'Разрыв поступлений',
        collectionRate: 'Уровень поступлений',
        netCashFlow: 'Чистый денежный поток',
        cashIn: 'Поступления',
        cashOut: 'Списания',
        overdue: 'Просрочено к оплате',
        asOf: 'на {date}',
        thisMonth: 'Этот месяц',
        unreconciled: 'Найдены расхождения при сверке',
        reconciled: 'Учёт сверен',
        quickLinks: 'Перейти',
        quickReceivables: 'Остатки клиентов',
        quickReceivablesHint: 'Открытые остатки и поступления',
        quickPayables: 'Счета поставщиков',
        quickPayablesHint: 'Регистрация и оплата счетов поставщиков',
        quickAccounts: 'Счета учёта',
        quickAccountsHint: 'Кассы, банки и их проводки',
        quickCosting: 'Себестоимость продукции',
        quickCostingHint: 'Снимки расчёта себестоимости по продуктам',
        recentPayments: 'Последние платежи',
        noPayments: 'Платежи пока не зарегистрированы.',
        warnings: 'Предупреждения',
        loadError: 'Не удалось загрузить обзор финансов.',
      },
      accounts: {
        title: 'Счета учёта',
        newAccount: 'Новый счёт',
        editAccount: 'Изменить счёт',
        accountCode: 'Код счёта',
        accountName: 'Название',
        accountType: 'Тип счёта',
        department: 'Подразделение',
        branch: 'Филиал',
        transactions: 'Проводки',
        transactionsFor: 'Проводки — {name}',
        ledgerTitle: 'Реестр счёта',
        entryKind: 'Тип проводки',
        postedAt: 'Проведено',
        noTransactions: 'По этому счёту ещё нет проводок.',
        netByCurrency: 'Итоговая позиция по валютам',
        activeAccount: 'Активен',
        accountSaved: 'Счёт сохранён.',
        loadError: 'Не удалось загрузить счета учёта.',
        empty: 'Счетов пока нет. Создайте кассу или банковский счёт.',
        deleteHint: 'Счета не удаляются — деактивируйте их.',
      },
      transfers: {
        title: 'Переводы между счетами',
        newTransfer: 'Новый перевод',
        sourceAccount: 'Со счёта',
        destinationAccount: 'На счёт',
        sourceAmount: 'Сумма списания',
        destinationAmount: 'Сумма зачисления',
        feeAmount: 'Комиссия',
        feeAccount: 'Счёт комиссии',
        transferDate: 'Дата перевода',
        reversed: 'Перевод сторнирован.',
        reverse: 'Сторнировать',
        reverseConfirm: 'Сторнировать перевод? Связанные проводки также будут сторнированы.',
        empty: 'Переводов пока нет.',
        loadError: 'Не удалось загрузить переводы.',
        sameAccount: 'Выберите два разных счёта.',
        detailTitle: 'Перевод №{id}',
      },
      picker: {
        accountTitle: 'Выбор счёта',
        accountSearch: 'Поиск счёта по названию или коду…',
        invoiceTitle: 'Выбор счёта клиента',
        invoiceSearch: 'Поиск счёта по номеру или контрагенту…',
        agentTitle: 'Выбор контрагента',
        agentEyebrow: 'Контрагенты',
        agentSearch: 'Контрагент',
        agentPlaceholder: 'Поиск по названию или телефону…',
        agentEmpty: 'Контрагенты не найдены.',
        agentLoading: 'Загрузка контрагентов…',
        productTitle: 'Выбор продукта',
        productEyebrow: 'Продукты',
        productSearch: 'Продукт',
        productPlaceholder: 'Поиск по названию или артикулу…',
        productEmpty: 'Продукты не найдены.',
        productLoading: 'Загрузка продуктов…',
        expenseTitle: 'Выберите накладной расход',
        expenseEyebrow: 'Справочник накладных расходов',
        expenseSearch: 'Накладной расход',
        expensePlaceholder: 'Поиск по названию или коду…',
        expenseEmpty: 'Накладные расходы не найдены.',
        expenseLoading: 'Загрузка накладных расходов…',
        roleCustomer: 'Клиент',
        roleSupplier: 'Поставщик',
        page: 'Стр. {page} из {total}',
        pageRange: '{from}–{to} из {total}',
        close: 'Закрыть',
        retry: 'Повторить',
        previous: 'Назад',
        next: 'Вперёд',
        cancel: 'Отмена',
      },
      reports: {
        period: 'Период',
        groupBy: 'Группировка',
        cashIn: 'Поступления',
        cashOut: 'Списания',
        net: 'Итог',
        netTotal: 'Чистый денежный поток',
        receivablesAmount: 'Дебиторка',
        payablesAmount: 'Кредиторка',
        cashAmount: 'Денежные средства',
        customerAdvances: 'Авансы клиентов',
        netExposure: 'Чистая позиция',
        fxRate: 'Курс',
        baseCurrency: 'Базовая валюта',
        currenciesCount: 'Валют',
        warnings: 'Предупреждения',
        reconciled: 'Сверено',
        notReconciled: 'Расхождения',
        paymentPostingDelta: 'Расхождение проводок',
        receivablesBalance: 'Остаток дебиторки',
        payablesBalance: 'Остаток кредиторки',
        treasuryBalance: 'Остаток кассы и банка',
        activeAccountedPayments: 'Учтённые платежи',
        activePaymentPostings: 'Проводки платежей',
        empty: 'Нет данных за выбранный период.',
        loadError: 'Не удалось загрузить отчёт.',
        invoiced: 'Выставлено',
        collected: 'Поступило',
        gap: 'Разрыв',
        rate: 'Уровень поступлений',
        allocatedCollections: 'Распределённые поступления',
        unallocatedCollections: 'Нераспределённые поступления',
        refunds: 'Возвраты',
        creditNotes: 'Кредит-ноты',
        writeOffs: 'Списания',
        debitNotes: 'Дебет-ноты',
      },
      receivables: {
        title: 'Счета клиентам',
        detailTitle: 'Счёт №{id}',
        order: 'Заказ',
        shipment: 'Отгрузка',
        amount: 'Сумма',
        paid: 'Оплачено',
        outstanding: 'К оплате',
        created: 'Создан',
        lines: 'Позиции счёта',
        allocations: 'Распределение платежей',
        product: 'Продукт',
        sku: 'Артикул',
        quantity: 'Количество',
        unitPrice: 'Цена',
        lineTotal: 'Сумма строки',
        allocationDate: 'Дата распределения',
        allocatedAmount: 'Распределено',
        noAllocations: 'По этому счёту ещё нет распределений платежей.',
        empty: 'Счета не найдены.',
        loadError: 'Не удалось загрузить счета.',
      },
      payments: {
        newPayment: 'Новый платёж',
        editPayment: 'Изменить платёж',
        reverse: 'Сторнировать',
        reverseConfirm: 'Сторнировать платёж? Проводка и распределения по счетам будут сторнированы, запись удалена.',
        reversed: 'Платёж сторнирован.',
        empty: 'Платежи не найдены.',
        allocationIncomplete: 'Для каждого распределения нужен счёт и сумма больше нуля.',
        allocationExceeds: 'Распределённая сумма превышает сумму платежа.',
        addAllocation: 'Добавить счёт',
        allocatedTotal: 'Распределено',
        unallocated: 'Не распределено',
        selectCounterparty: 'Выберите контрагента, чтобы загрузить открытые счета.',
        noOpenInvoices: 'У выбранного контрагента нет открытых счетов.',
        selectAllocationInvoice: 'Выберите счёт',
        recordForInvoice: 'Принять оплату',
        advanceNotice: 'Платёж без распределения учитывается как аванс клиента.',
        loadError: 'Не удалось загрузить платежи.',
      },
      adjustments: {
        title: 'Корректировки счетов',
        newAdjustment: 'Новая корректировка',
        adjustmentNumber: 'Номер корректировки',
        numberHint: 'Обязателен и должен быть уникальным.',
        reasonHint: 'Корректировка исправляет выставленный счёт без платежа.',
        refundAccountRequired: 'Для возврата нужен счёт учёта в валюте счёта клиента.',
        refundAccountHint: 'Возврат проводится по этому счёту.',
        statusHint: 'Проведённые корректировки сразу меняют остаток счёта клиента.',
        selectInvoice: 'Выберите счёт',
        noInvoices: 'У этого контрагента нет счетов для корректировки.',
        post: 'Провести',
        postConfirm: 'Провести корректировку? Остаток счёта и проводка будут обновлены.',
        posted: 'Корректировка проведена.',
        reverse: 'Сторнировать',
        reverseConfirm: 'Сторнировать корректировку? Остаток счёта и проводка будут восстановлены.',
        reversed: 'Корректировка сторнирована.',
        empty: 'Корректировки не найдены.',
        loadError: 'Не удалось загрузить корректировки.',
      },
      payables: {
        supplierInvoicesTitle: 'Счета поставщиков',
        newInvoice: 'Новый счёт поставщика',
        editInvoice: 'Изменить счёт поставщика',
        supplier: 'Поставщик',
        invoiceNumber: 'Номер счёта',
        invoiceDate: 'Дата счёта',
        numberHint: 'Должен быть уникальным для поставщика.',
        purchaseOrder: 'Заказ на закупку',
        noPurchaseOrders: 'Нет подходящих заказов для этого поставщика и валюты.',
        purchaseOrdersUnavailable: 'Заказы недоступны без права на просмотр закупок.',
        department: 'Подразделение',
        warehouse: 'Склад',
        openTotal: 'К оплате',
        statusHint: 'Статус оплаты рассчитывается по распределениям платежей; вручную можно задать только аннулирование.',
        dueBeforeInvoice: 'Срок оплаты не может быть раньше даты счёта.',
        void: 'Аннулировать',
        voidConfirm: 'Аннулировать счёт поставщика? Он останется в реестре, но будет считаться отменённым.',
        voided: 'Счёт поставщика аннулирован.',
        deleteConfirm: 'Удалить счёт поставщика? Запись будет удалена из реестра.',
        noAllocations: 'По этому счёту ещё нет распределений платежей.',
        empty: 'Счета поставщиков не найдены.',
        loadError: 'Не удалось загрузить счета поставщиков.',
        agingTitle: 'Просрочка к оплате',
        totalPayables: 'Всего к оплате',
        overdueDays: 'Дней просрочки',
        bucket: 'Группа просрочки',
        agingEmpty: 'По выбранным фильтрам нет непогашенной задолженности.',
      },
      costing: {
        title: 'Себестоимость продукции',
        selectProduct: 'Выбрать продукт',
        productPrompt: 'Выберите продукт, чтобы загрузить расчёты себестоимости.',
        productionType: 'Тип производства',
        productionTypePlaceholder: 'Фильтр по типу производства…',
        version: 'Версия формулы',
        calculatedBy: 'Рассчитал',
        calculatedAt: 'Дата расчёта',
        baseCost: 'Базовая стоимость',
        wasteCost: 'Отходы',
        costBeforeProfit: 'Стоимость до прибыли',
        profitCost: 'Прибыль',
        finalCost: 'Итоговая стоимость',
        standardCost: 'Последняя стоимость (норматив)',
        variance: 'Изменение к предыдущей',
        noVariance: 'Нет более раннего расчёта',
        components: 'Составляющие стоимости',
        component: 'Составляющая',
        componentType: 'Тип',
        group: 'Группа',
        amount: 'Сумма',
        quantityPerUnit: 'Кол-во на единицу',
        sourceUnitCost: 'Цена источника',
        noComponents: 'В этом расчёте нет составляющих.',
        marginAnalysis: 'Анализ маржи',
        pricelist: 'Прайс-лист',
        noPricelist: 'Прайс-лист недоступен',
        price: 'Цена продажи',
        noPrice: 'Для этого продукта нет цены в выбранном прайс-листе.',
        margin: 'Маржа',
        marginRate: 'Маржинальность',
        currencyMismatch: 'Валюта цены отличается от валюты себестоимости — маржа несопоставима.',
        empty: 'Для этого продукта ещё нет расчётов себестоимости.',
        loadError: 'Не удалось загрузить расчёты себестоимости.',
      },
      productCost: {
        title: 'Себестоимость продукции',
        subtitle: 'Версии, источники и согласование',
        backToCosting: 'Исторические калькуляции',
        refreshList: 'Обновить список',
        allProducts: 'Все продукты',
        find: 'Найти',
        newDraft: 'Новый черновик',
        versions: 'Версии',
        noVersions: 'Версий пока нет',
        versionHeading: '{product} · версия {version}',
        batch: 'Партия',
        expectedOutput: 'Ожидаемый выпуск',
        yield: 'Выход',
        checkSources: 'Проверить источники',
        refreshVersion: 'Обновить версию',
        edit: 'Редактировать',
        clone: 'Создать копию',
        pricingLegend: 'Предлагаемая цена при следующем расчёте',
        pricingMethod: 'Метод',
        pricing: {
          markup: 'Наценка % к себестоимости',
          targetMargin: 'Целевая маржа % от цены',
          manual: 'Цена вручную',
          markupShort: 'Наценка',
          marginShort: 'Маржа',
          manualShort: 'Цена вручную',
        },
        pricingNote: 'Наценка и маржа не включаются в производственную себестоимость.',
        sourceQuality: 'Качество источников',
        calculable: 'Расчёт возможен',
        noIssues: 'Источники проверены, замечаний нет.',
        blocking: 'Блокирует расчёт',
        warning: 'Предупреждение',
        details: 'Подробности',
        bomComposition: 'Состав BOM',
        bomSummary: '{materials} материал(ов) · {operations} операц.',
        bomNote: 'Показаны все строки BOM независимо от результата расчёта. Закупочная цена приоритетнее цены оператора.',
        material: 'Материал',
        qtyPerBatch: 'Кол-во на партию',
        unit: 'Ед.',
        sourcePrice: 'Цена источника',
        priceSource: 'Источник цены',
        operatorPrice: 'Цена оператора',
        operatorRate: 'Ставка оператора',
        materialMeta: '{sku} · операция {sequence}',
        operation: 'Операция',
        durationMinutes: 'Длительность, мин',
        rate: 'Ставка',
        rateSource: 'Источник ставки',
        noBomMaterials: 'В BOM нет материалов.',
        noBomOperations: 'В BOM нет операций.',
        operatorPricesNote: 'Изменённые цены и ставки оператора отправляются вместе с проверкой источников и расчётом.',
        priceMissing: 'не найдена — укажите цену оператора',
        pricePurchaseFromOrders: 'закупка (из заказов)',
        pricePurchase: 'закупочная цена',
        priceOperator: 'цена оператора',
        purchaseInUse: 'используется закупочная',
        enterPrice: 'введите цену',
        enterRate: 'введите ставку',
        rateMissing: 'не найдена',
        elementKnitting: 'Вязальные материалы',
        elementOtherMaterial: 'Прочие материалы',
        elementPackaging: 'Упаковочные материалы',
        elementLabour: 'Прямой труд',
        elementMachine: 'Машины',
        elementSubcontracting: 'Субподряд',
        elementVariableOverhead: 'Переменные накладные',
        elementFixedOverhead: 'Постоянные накладные',
        elementQuality: 'Качество и брак',
        elementOtherCost: 'Прочие прямые затраты',
        priceOverrideIgnored: 'Используется закупочная цена {price} {currency}; цена оператора не применяется.',
        priceAppliedDifferent: 'API применил {applied} вместо введённых {typed}.',
        serverGroupingNote: 'Итоги по материалам сгруппированы по статье API; вкладки группируют строки по операции BOM.',
        tabsLabel: 'Разделы калькуляции',
        tab: {
          knitting: 'Вязальные материалы',
          materials: 'Прочие материалы',
          operations: 'Операции',
          expenses: 'Расходы',
          warnings: 'Предупреждения',
          summary: 'Итоги',
          audit: 'Аудит',
        },
        lines: {
          item: 'Статья / источник',
          quantity: 'Количество',
          sourceRate: 'Ставка источника',
          currencyId: 'Валюта ID',
          fx: 'FX',
          costRate: 'Ставка {currency}',
          amount: 'Сумма',
          origin: 'Происхождение',
        },
        noLines: 'Нет рассчитанных строк в этом разделе.',
        overridesGap: 'Ручные overrides пока не поддерживаются API. Исправьте исходные данные и повторите расчёт.',
        serverTotals: 'Итоги сервера · {currency}',
        totals: {
          item: 'Статья',
          perBatch: 'На партию',
          perUnit: 'На единицу',
        },
        proposedPrices: 'Предлагаемые цены',
        prices: {
          method: 'Метод',
          value: 'Значение',
          unitPrice: 'Цена за единицу',
        },
        publishGap: 'Публикация в прайс-лист недоступна до подключения соответствующего API.',
        comparableVersion: 'Сопоставимая версия',
        selectVersion: 'Выберите версию',
        compare: 'Сравнить',
        comparisonHint: 'Показаны сопоставимые версии из последних 200 калькуляций продукта.',
        unitCostDelta: 'Изменение себестоимости единицы (сервер)',
        comparison: {
          item: 'Статья',
          quantityLeft: 'Количество слева',
          quantityRight: 'Количество справа',
          amountLeft: 'Сумма слева',
          amountRight: 'Сумма справа',
        },
        added: 'Добавлена',
        removed: 'Удалена',
        approvalData: 'Данные согласования',
        approvalGap: 'Показаны поля версии. Полная история переходов требует отдельного API аудита.',
        auditFields: {
          createdByUserId: 'Создал (ID пользователя)',
          createdAt: 'Создано',
          calculatedByUserId: 'Рассчитал (ID пользователя)',
          calculatedAt: 'Рассчитано',
          submittedByUserId: 'Отправил (ID пользователя)',
          submittedAt: 'Отправлено',
          approvedByUserId: 'Одобрил (ID пользователя)',
          approvedAt: 'Одобрено',
          publishedByUserId: 'Опубликовал (ID пользователя)',
          publishedAt: 'Опубликовано',
          rowVersion: 'Версия строки',
          calculationChecksum: 'Контрольная сумма расчёта',
        },
        selectVersionPrompt: 'Выберите версию или создайте новый черновик.',
        publishConfirm: 'Опубликовать эту версию? Предыдущий стандарт может быть замещён.',
        calculateConfirm: 'Пересчитать и заменить сохранённые строки калькуляции?',
        invalidPricing: 'Укажите неотрицательное значение; целевая маржа должна быть меньше 100%.',
        incompatibleVersions: 'Версии имеют разные параметры или валюты. Обновите данные.',
        status: {
          draft: 'Черновик',
          calculated: 'Рассчитано',
          pending_approval: 'На согласовании',
          approved: 'Одобрено',
          published: 'Опубликовано',
          rejected: 'Отклонено',
        },
        guidance: {
          bom_missing: 'Создайте BOM для продукта или выберите другую версию.',
          material_uom_missing: 'Настройте одну активную базовую единицу измерения материала.',
          material_price_missing: 'Укажите закупочную цену, действующую на дату калькуляции.',
          material_price_stale: 'Проверьте актуальность закупочной цены и обновите её при необходимости.',
          fx_rate_missing: 'Добавьте утверждённый бухгалтерский курс на дату калькуляции.',
          operation_rate_missing: 'Укажите ставку операции для выбранной смены.',
          overhead_rule_conflict: 'Устраните пересечение утверждённых правил распределения расходов.',
        },
        guidanceFallback: 'Проверьте исходные данные перед расчётом.',
        action: {
          calculate: 'Рассчитать',
          submit: 'На согласование',
          approve: 'Одобрить',
          reject: 'Отклонить',
          publish: 'Опубликовать',
        },
        warningsPrompt: 'Запустите проверку источников, чтобы получить актуальные предупреждения.',
        overheads: {
          title: 'Накладные расходы',
          help: 'Одобренные правила применяются при следующей проверке источников или расчёте. Черновики сохраняются, но не учитываются.',
          add: 'Добавить накладной расход',
          expense: 'Накладной расход',
          selectExpense: 'Выберите накладной расход',
          empty: 'Правила накладных расходов не найдены.',
          noneApplicable: 'Для этой версии нет применимых правил накладных расходов.',
          noPermission: 'Для просмотра правил требуется право cost_overheads.view.',
          loading: 'Загрузка правил накладных расходов…',
          code: 'Код',
          name: 'Название',
          basis: 'База распределения',
          rateType: 'Тип ставки',
          rateValue: 'Ставка',
          percent: 'Процент',
          amount: 'Сумма',
          currency: 'Валюта',
          scope: 'Область',
          scopeProduct: 'Этот продукт',
          scopeAll: 'Все продукты',
          scopeGroup: 'Группа продуктов',
          scopeDepartment: 'Подразделение',
          effectiveFrom: 'Начало действия',
          effectiveTo: 'Окончание действия',
          priority: 'Приоритет',
          approveNow: 'Одобрить сразу',
          save: 'Сохранить накладной расход',
          cancel: 'Отмена',
          approve: 'Одобрить',
          statusDraft: 'Черновик',
          statusApproved: 'Одобрено',
          statusRetired: 'Выведено',
          basisDirectMaterialPercent: 'Прямые материалы, %',
          basisDirectLaborPercent: 'Прямой труд, %',
          basisPrimeCostPercent: 'Производственная себестоимость, %',
          basisPerProducedUnit: 'На единицу продукции',
          basisPerMachineHour: 'На машино-час',
          basisPerLaborHour: 'На человеко-час',
          basisFixedPerBatch: 'Фиксированно на партию',
          invalid: 'Укажите код, название, базу, положительную ставку и дату начала.',
          currencyRequired: 'Выберите валюту для суммовой ставки.',
        },
        editor: {
          titleCreate: 'Новая версия',
          titleEdit: 'Редактирование черновика',
          titleClone: 'Копия версии',
          cloneNote: 'Создаётся новый черновик исходных допущений. Рассчитайте его для получения актуальных источников и сумм.',
          permissions: 'Для выбора справочников требуются права products.view и bom.view.',
          retryLoad: 'Повторить загрузку',
          product: 'Продукт',
          selectProduct: 'Выберите продукт',
          bomVersion: 'Версия BOM',
          selectBom: 'Выберите BOM',
          active: 'активная',
          inactive: 'неактивная',
          currency: 'Валюта',
          selectCurrency: 'Выберите валюту',
          scenarioName: 'Название сценария',
          productionType: 'Тип производства',
          costingDate: 'Дата калькуляции',
          effectiveFrom: 'Начало действия',
          effectiveTo: 'Окончание действия',
          batchQuantity: 'Размер партии',
          expectedOutputQuantity: 'Ожидаемый выпуск',
          yieldRate: 'Коэффициент выхода',
          costType: 'Тип',
          costTypeStandard: 'Стандартная',
          costTypePlanned: 'Плановая',
          shift: 'Смена',
          shiftDay: 'День',
          shiftNight: 'Ночь',
          shiftBlended: 'Смешанная',
          notes: 'Примечание',
          save: 'Сохранить',
          cancel: 'Отмена',
          noBoms: 'Для продукта не найдены версии BOM.',
          productBomRequired: 'Выберите продукт и его версию BOM.',
          currencyRequired: 'Выберите валюту.',
          positiveNumbers: 'Количество и выход должны быть положительными числами, до 8 знаков после точки.',
          yieldMax: 'Коэффициент выхода не должен превышать 1.',
          periodInvalid: 'Проверьте период действия.',
        },
      },
      payroll: {
        title: 'Зарплатные затраты',
        gapTitle: 'Расчётные ведомости формируются из выработки и корректировок',
        gapNotice: 'API зарплаты покрывает периоды, начисления за труд, корректировки, расчётные ведомости и проводки — используйте разделы зарплаты. Эта страница остаётся отчётом о затратах на труд из табелей за тот же период.',
        labourCost: 'Затраты на труд',
        hours: 'Часы',
        cycles: 'Циклы',
        employees: 'Сотрудники',
        entries: 'Записи',
        employee: 'Сотрудник',
        period: 'Период',
        total: 'Итого',
        empty: 'За выбранный период записей в табелях нет.',
        loadError: 'Не удалось загрузить отчёт по затратам на труд.',
      },
      payrollFlow: {
        allPeriods: 'Все периоды',
        selectPeriod: 'Выберите период',
        approve: 'Утвердить',
        reject: 'Отклонить',
        reverse: 'Отменить',
        rejectTitle: 'Отклонить запись',
        reverseTitle: 'Отменить запись',
        rejectMessage: 'Причина сохраняется вместе с отклонённой записью.',
        reverseMessage: 'Причина отмены попадает в журнал аудита.',
        statusOpen: 'Открыт',
        statusLocked: 'Закрыт для правок',
        statusClosed: 'Закрыт',
        employeePickerTitle: 'Выберите сотрудника',
        employeePickerEyebrow: 'Зарплата',
        employeePickerSearch: 'Сотрудник',
        employeePickerPlaceholder: 'Поиск по имени или табельному номеру…',
        employeePickerEmpty: 'Производственные сотрудники не найдены.',
        periods: {
          eyebrow: 'Зарплата',
          title: 'Периоды зарплаты',
          description: 'Периоды задают окно расчёта. Редактировать можно только открытые периоды; блокировка сохраняет начисления, но фиксирует даты.',
          newPeriod: 'Новый период',
          editPeriod: 'Изменить период',
          formHint: 'Периоды не могут пересекаться, а дата окончания не может быть раньше даты начала.',
          code: 'Код',
          name: 'Название',
          range: 'Период',
          lockedAt: 'Заблокирован',
          lock: 'Заблокировать',
          close: 'Закрыть',
          lockConfirm: 'Заблокировать {code}? Начисления по нему всё ещё можно добавлять.',
          closeConfirm: 'Закрыть {code}? Закрытие необратимо.',
          created: 'Период создан.',
          updated: 'Период обновлён.',
          locked: 'Период заблокирован.',
          closed: 'Период закрыт.',
          empty: 'Периодов зарплаты ещё нет.',
        },
        accruals: {
          eyebrow: 'Зарплата',
          title: 'Начисления за труд',
          description: 'Выработка, которая создаётся при завершении рабочей сессии с готовой продукцией. Утверждение делает её выплачиваемой в расчётной ведомости.',
          employee: 'Сотрудник',
          production: 'Производство',
          completed: 'Выполнено',
          accepted: 'Принято',
          rejected: 'Брак',
          rework: 'Переделка',
          rate: 'Расценка',
          gross: 'Начислено',
          doneApproved: 'Начисление для {employee} утверждено.',
          doneRejected: 'Начисление отклонено.',
          doneReversed: 'Начисление отменено.',
          empty: 'По выбранным фильтрам начислений нет.',
        },
        accrualStatusProvisional: 'Предварительное',
        accrualStatusApproved: 'Утверждено',
        accrualStatusRejected: 'Отклонено',
        accrualStatusExported: 'В ведомости',
        accrualStatusPaid: 'Выплачено',
        accrualStatusReversed: 'Отменено',
        adjustments: {
          eyebrow: 'Зарплата',
          title: 'Корректировки зарплаты',
          description: 'Ручные премии, удержания, авансы, штрафы и исправления. Премия и исправление становятся начислениями, остальные — удержаниями.',
          create: 'Новая корректировка',
          employee: 'Сотрудник',
          selectEmployee: 'Выберите сотрудника для корректировки.',
          period: 'Период',
          periodRequired: 'Выберите период зарплаты.',
          amountRequired: 'Введите сумму больше нуля.',
          currencyRequired: 'Выберите валюту.',
          typeHint: 'Премия и исправление увеличивают начисление, а удержание, аванс и штраф уменьшают сумму к выплате.',
          created: 'Корректировка создана.',
          doneApproved: 'Корректировка утверждена.',
          doneRejected: 'Корректировка отклонена.',
          doneReversed: 'Корректировка отменена.',
          empty: 'По выбранным фильтрам корректировок нет.',
        },
        adjustmentTypeBonus: 'Премия',
        adjustmentTypeDeduction: 'Удержание',
        adjustmentTypeAdvance: 'Аванс',
        adjustmentTypePenalty: 'Штраф',
        adjustmentTypeCorrection: 'Исправление',
        adjustmentStatusDraft: 'Черновик',
        adjustmentStatusApproved: 'Утверждено',
        adjustmentStatusRejected: 'Отклонено',
        adjustmentStatusUsed: 'Использовано',
        adjustmentStatusReversed: 'Отменено',
        runs: {
          eyebrow: 'Зарплата',
          title: 'Расчётные ведомости',
          description: 'Одна ведомость на период и валюту. Ведомость собирает утверждённые начисления и корректировки в строки по сотрудникам.',
          generate: 'Сформировать ведомость',
          generateHint: 'Валюты никогда не объединяются — формируйте отдельную ведомость на каждую валюту.',
          period: 'Период',
          periodRequired: 'Выберите период зарплаты.',
          currencyRequired: 'Выберите валюту.',
          code: 'Код',
          codeHint: 'Необязательно — бэкенд сам создаст PAYRUN-{period}-{currency}, если оставить пустым.',
          range: 'Период',
          gross: 'Начислено',
          deductions: 'Удержания',
          net: 'К выплате',
          lines: 'Строки',
          empty: 'Расчётных ведомостей ещё нет.',
          generated: 'Ведомость {code} сформирована.',
          generatedPlain: 'Ведомость сформирована.',
          recalculate: 'Пересчитать',
          approve: 'Утвердить ведомость',
          post: 'Провести ведомость',
          recalculated: 'Ведомость пересчитана.',
          approvedRun: 'Ведомость утверждена.',
          posted: 'Ведомость проведена по счёту.',
          accountRequired: 'Выберите финансовый счёт.',
          postConfirm: 'Провести ведомость {code}? Будет создан расход по кассе на каждого сотрудника.',
          account: 'Финансовый счёт',
          accountHint: 'Проводить можно только утверждённые ведомости, и валюта счёта должна совпадать с валютой ведомости.',
          accountCurrencyMismatch: 'Этот счёт не в {currency}.',
          detailTitle: 'Расчётная ведомость',
          lineTitle: 'Строки по сотрудникам',
          noLines: 'В этой ведомости ещё нет строк по сотрудникам.',
          employee: 'Сотрудник',
          department: 'Подразделение',
          earnings: 'Начисления',
          deductionLines: 'Удержания',
          quantity: 'Количество',
          amount: 'Сумма',
        },
        runStatusDraft: 'Черновик',
        runStatusApproved: 'Утверждена',
        runStatusPosted: 'Проведена',
        runStatusPaid: 'Выплачена',
        runStatusReversed: 'Отменена',
        postings: {
          eyebrow: 'Зарплата',
          title: 'Проводки зарплаты',
          description: 'Проведённые суммы к выплате по сотрудникам и связанная кассовая операция. Отмена возвращает деньги на счёт.',
          employee: 'Сотрудник',
          run: 'Ведомость',
          account: 'Счёт',
          net: 'К выплате',
          postedAt: 'Проведено',
          doneReversed: 'Проводка по {employee} отменена.',
          empty: 'Проводок зарплаты ещё нет.',
        },
        postingStatusPosted: 'Проведено',
        postingStatusPaid: 'Выплачено',
        postingStatusReversed: 'Отменено',
        reports: {
          eyebrow: 'Зарплата',
          title: 'Отчёты по зарплате',
          description: 'Только чтение: итоги и сверки за выбранный период, валюту и диапазон дат.',
          runNet: 'Итого к выплате',
          runGross: 'Начислено',
          runDeductions: 'Удержания',
          runCount: 'Ведомости',
          employeeCount: 'Сотрудники',
          section: 'Раздел',
          rows: 'Строк',
          gross: 'Начислено',
          settled: 'Закрыто',
          open: 'Открыто',
          runs: 'Ведомости',
          accruals: 'Начисления',
          postings: 'Проводки',
          empty: 'По выбранным фильтрам данных по зарплате нет.',
          accrualReconciliation: 'Сверка начислений',
          accrualReconciliationHint: 'Итоги начислений сравниваются со строками начислений ведомостей.',
          postingReconciliation: 'Сверка проводок',
          postingReconciliationHint: 'Итоги ведомостей сравниваются с активными проводками зарплаты.',
          reconciled: 'Расхождения нет.',
          delta: 'Расхождение {amount} требует проверки.',
          noRows: 'По этим фильтрам сверять нечего.',
          accrualGross: 'Начислено по начислениям',
          payrollEarnings: 'Начисления в ведомостях',
          unexportedApproved: 'Утверждено, но не в ведомости',
          activePostings: 'Активные проводки',
          reversedPostings: 'Отменённые проводки',
          deltaColumn: 'Расхождение',
        },
        statement: {
          eyebrow: 'Зарплата',
          title: 'Расчётный лист сотрудника',
          description: 'Начисления, удержания и проводки по одному сотруднику. Сотрудник обязателен — отчёт не принимает пустой фильтр.',
          selectEmployee: 'Выберите сотрудника, чтобы увидеть расчётный лист.',
          noEarnings: 'За выбранный период начислений по сотруднику нет.',
          noDeductions: 'За выбранный период удержаний по сотруднику нет.',
          noPostings: 'За выбранный период проводок по сотруднику нет.',
        },
      },
    },
  },
  uz: {
    finance: {
      appTitle: 'Moliya',
      appDescription: 'Qabul qilinadigan va to‘lanadigan qarzlar, kassa va bank, tannarx va moliyaviy hisobotlar.',
      nav: {
        overview: 'Umumiy ko‘rinish',
        receivables: 'Qabul qilinadigan qarzlar',
        payables: 'To‘lanadigan qarzlar',
        treasury: 'Kassa va bank',
        costing: 'Tannarx',
        productCostWorkspace: 'Mahsulot tannarxi',
        payroll: 'Ish haqi',
        payrollPeriods: 'Davrlar',
        payrollAccruals: 'Mehnat hisoblari',
        payrollAdjustments: 'Tuzatishlar',
        payrollRuns: 'Hisob-kitoblar',
        payrollPostings: 'O‘tkazmalar',
        payrollReports: 'Hisobotlar',
        payrollStatement: 'Xodim hisoboti',
        labourCost: 'Mehnat xarajati',
        invoices: 'Hisob-fakturalar',
        collections: 'Tushumlar',
        revenueVsCollections: 'Tushum va savdo',
        adjustments: 'Tuzatishlar',
        supplierInvoices: 'Ta’minotchi hisob-fakturalari',
        supplierPayments: 'Ta’minotchiga to‘lovlar',
        payablesAging: 'Muddati o‘tgan qarzlar',
        accounts: 'Hisobvaraqlar',
        transfers: 'O‘tkazmalar',
        cashFlow: 'Pul oqimi',
        currencyExposure: 'Valyuta pozitsiyasi',
        reconciliation: 'Solishtirish',
      },
      common: {
        actions: 'Amallar',
        open: 'Ochish',
        cancel: 'Bekor qilish',
        save: 'Saqlash',
        saving: 'Saqlanmoqda…',
        create: 'Yaratish',
        edit: 'Tahrirlash',
        delete: 'O‘chirish',
        retry: 'Qayta urinish',
        refresh: 'Yangilash',
        loading: 'Yuklanmoqda…',
        empty: 'Ma’lumot yo‘q.',
        search: 'Qidirish',
        searchPlaceholder: 'Nom, kod yoki izoh bo‘yicha qidirish…',
        apply: 'Qo‘llash',
        reset: 'Tozalash',
        clear: 'Tozalash',
        all: 'Barchasi',
        allStatuses: 'Barcha holatlar',
        allTypes: 'Barcha turlar',
        allCurrencies: 'Barcha valyutalar',
        allMethods: 'Barcha usullar',
        allAccounts: 'Barcha hisobvaraqlar',
        none: 'Yo‘q',
        optional: 'ixtiyoriy',
        yes: 'Ha',
        no: 'Yo‘q',
        active: 'Faol',
        inactive: 'Nofaol',
        status: 'Holat',
        type: 'Turi',
        date: 'Sana',
        dueDate: 'To‘lov muddati',
        dateFrom: 'Boshlanish',
        dateTo: 'Tugash',
        asOf: 'Sanaga',
        amount: 'Summa',
        currency: 'Valyuta',
        exchangeRate: 'Kurs',
        total: 'Jami',
        balance: 'Qoldiq',
        openingBalance: 'Boshlang‘ich qoldiq',
        currentBalance: 'Joriy qoldiq',
        reference: 'Izoh raqami',
        notes: 'Izohlar',
        reason: 'Sabab',
        agent: 'Kontragent',
        account: 'Hisobvaraq',
        method: 'Usul',
        direction: 'Yo‘nalish',
        invoice: 'Hisob-faktura',
        invoices: 'Hisob-fakturalar',
        payments: 'To‘lovlar',
        rows: '{count} qator',
        page: '{page} / {total} sahifa',
        previous: 'Oldingi',
        next: 'Keyingi',
        back: 'Orqaga',
        close: 'Yopish',
        noPermission: 'Bu amal uchun ruxsat yo‘q.',
        invalidRange: 'Boshlanish sanasi tugash sanasidan keyin bo‘lishi mumkin emas.',
        requiredField: '«{field}» maydoni to‘ldirilishi shart.',
        saved: 'Saqlandi.',
        deleted: 'O‘chirildi.',
        detail: 'Tafsilotlar',
        allDone: 'Tugallandi',
        showing: '{from}–{to} / {total} ko‘rsatilmoqda',
      },
      paymentMethod: {
        cash: 'Naqd',
        card: 'Karta',
        transfer: 'Bank o‘tkazmasi',
      },
      paymentDirection: {
        inflow: 'Kirim',
        outflow: 'Chiqim',
      },
      invoiceStatus: {
        draft: 'Qoralama',
        unpaid: 'To‘lanmagan',
        partial: 'Qisman to‘langan',
        paid: 'To‘langan',
      },
      supplierInvoiceStatus: {
        open: 'Ochiq',
        partial: 'Qisman',
        closed: 'Yopilgan',
        void: 'Bekor qilingan',
        overdue: 'Muddati o‘tgan',
      },
      accountType: {
        cashbox: 'Kassa',
        bank: 'Bank',
        card: 'Karta',
        clearing: 'Kliring',
      },
      adjustmentType: {
        customer_credit: 'Mijozga kredit',
        write_off: 'Hisobdan chiqarish',
        debit_note: 'Debet-nota',
        refund: 'Qaytarish',
      },
      adjustmentStatus: {
        draft: 'Qoralama',
        posted: 'O‘tkazilgan',
        reversed: 'Bekor qilingan',
      },
      transferStatus: {
        posted: 'O‘tkazilgan',
        reversed: 'Bekor qilingan',
      },
      entryKind: {
        payment: 'To‘lov',
        payment_reversal: 'To‘lovni bekor qilish',
        transfer: 'O‘tkazma',
        transfer_reversal: 'O‘tkazmani bekor qilish',
        transfer_fee: 'O‘tkazma komissiyasi',
        transfer_fee_reversal: 'Komissiyani bekor qilish',
        adjustment: 'Tuzatish',
      },
      agingBucket: {
        current: 'Muddati o‘tmagan',
        days1_30: '1–30 kun',
        days31_60: '31–60 kun',
        days60Plus: '60+ kun',
      },
      groupBy: {
        day: 'Kunlik',
        week: 'Haftalik',
        month: 'Oylik',
      },
      overview: {
        title: 'Moliya umumiy ko‘rinishi',
        cashPosition: 'Kassa va bank',
        cashAccounts: 'Hisobvaraqlar',
        receivables: 'Qabul qilinadigan qarzlar',
        payables: 'To‘lanadigan qarzlar',
        collected: 'Tushum',
        invoiced: 'Hisob-faktura',
        collectionGap: 'Tushum farqi',
        collectionRate: 'Tushum darajasi',
        netCashFlow: 'Sof pul oqimi',
        cashIn: 'Kirim',
        cashOut: 'Chiqim',
        overdue: 'Muddati o‘tgan qarzlar',
        asOf: '{date} sanaga',
        thisMonth: 'Shu oy',
        unreconciled: 'Solishtirishda farqlar topildi',
        reconciled: 'Hisob solishtirilgan',
        quickLinks: 'O‘tish',
        quickReceivables: 'Mijoz qoldiqlari',
        quickReceivablesHint: 'Ochiq qoldiqlar va tushumlar',
        quickPayables: 'Ta’minotchi hisob-fakturalari',
        quickPayablesHint: 'Xarid hisob-fakturalarini ro‘yxatga olish',
        quickAccounts: 'Hisobvaraqlar',
        quickAccountsHint: 'Kassalar, banklar va ularning yozuvlari',
        quickCosting: 'Mahsulot tannarxi',
        quickCostingHint: 'Mahsulot bo‘yicha tannarx hisoblari',
        recentPayments: 'So‘nggi to‘lovlar',
        noPayments: 'Hozircha to‘lovlar yo‘q.',
        warnings: 'Ogohlantirishlar',
        loadError: 'Moliya umumiy ko‘rinishini yuklab bo‘lmadi.',
      },
      accounts: {
        title: 'Moliyaviy hisobvaraqlar',
        newAccount: 'Yangi hisobvaraq',
        editAccount: 'Hisobvaraqni tahrirlash',
        accountCode: 'Hisobvaraq kodi',
        accountName: 'Nomi',
        accountType: 'Hisobvaraq turi',
        department: 'Bo‘lim',
        branch: 'Filial',
        transactions: 'Yozuvlar',
        transactionsFor: 'Yozuvlar — {name}',
        ledgerTitle: 'Hisobvaraq yozuvlari',
        entryKind: 'Yozuv turi',
        postedAt: 'O‘tkazilgan vaqti',
        noTransactions: 'Bu hisobvaraqda hozircha yozuvlar yo‘q.',
        netByCurrency: 'Valyuta bo‘yicha yakuniy holat',
        activeAccount: 'Faol',
        accountSaved: 'Hisobvaraq saqlandi.',
        loadError: 'Hisobvaraqlarni yuklab bo‘lmadi.',
        empty: 'Hisobvaraqlar yo‘q. Kassa yoki bank hisobvarag‘ini yarating.',
        deleteHint: 'Hisobvaraqlar o‘chirilmaydi — ularni nofaol qiling.',
      },
      transfers: {
        title: 'Hisobvaraqlar orasidagi o‘tkazmalar',
        newTransfer: 'Yangi o‘tkazma',
        sourceAccount: 'Qaysi hisobvaraqdan',
        destinationAccount: 'Qaysi hisobvaraqqa',
        sourceAmount: 'Chiqim summasi',
        destinationAmount: 'Kirim summasi',
        feeAmount: 'Komissiya',
        feeAccount: 'Komissiya hisobvarag‘i',
        transferDate: 'O‘tkazma sanasi',
        reversed: 'O‘tkazma bekor qilindi.',
        reverse: 'Bekor qilish',
        reverseConfirm: 'O‘tkazma bekor qilinsinmi? Bog‘liq yozuvlar ham bekor qilinadi.',
        empty: 'Hozircha o‘tkazmalar yo‘q.',
        loadError: 'O‘tkazmalarni yuklab bo‘lmadi.',
        sameAccount: 'Ikki xil hisobvaraq tanlang.',
        detailTitle: 'O‘tkazma №{id}',
      },
      picker: {
        accountTitle: 'Hisobvaraq tanlash',
        accountSearch: 'Nom yoki kod bo‘yicha qidirish…',
        invoiceTitle: 'Hisob-faktura tanlash',
        invoiceSearch: 'Raqam yoki kontragent bo‘yicha qidirish…',
        agentTitle: 'Kontragent tanlash',
        agentEyebrow: 'Kontragentlar',
        agentSearch: 'Kontragent',
        agentPlaceholder: 'Nom yoki telefon bo‘yicha qidirish…',
        agentEmpty: 'Kontragentlar topilmadi.',
        agentLoading: 'Kontragentlar yuklanmoqda…',
        productTitle: 'Mahsulot tanlash',
        productEyebrow: 'Mahsulotlar',
        productSearch: 'Mahsulot',
        productPlaceholder: 'Nom yoki artikul bo‘yicha qidirish…',
        productEmpty: 'Mahsulotlar topilmadi.',
        productLoading: 'Mahsulotlar yuklanmoqda…',
        expenseTitle: 'Qo‘shimcha xarajatni tanlang',
        expenseEyebrow: 'Qo‘shimcha xarajatlar ma’lumotnomasi',
        expenseSearch: 'Qo‘shimcha xarajat',
        expensePlaceholder: 'Nomi yoki kodi bo‘yicha qidirish…',
        expenseEmpty: 'Qo‘shimcha xarajatlar topilmadi.',
        expenseLoading: 'Qo‘shimcha xarajatlar yuklanmoqda…',
        roleCustomer: 'Mijoz',
        roleSupplier: 'Ta’minotchi',
        page: '{page} / {total} sahifa',
        pageRange: '{from}–{to} / {total}',
        close: 'Yopish',
        retry: 'Qayta urinish',
        previous: 'Oldingi',
        next: 'Keyingi',
        cancel: 'Bekor qilish',
      },
      reports: {
        period: 'Davr',
        groupBy: 'Guruhlash',
        cashIn: 'Kirim',
        cashOut: 'Chiqim',
        net: 'Sof',
        netTotal: 'Sof pul oqimi',
        receivablesAmount: 'Qabul qilinadigan qarz',
        payablesAmount: 'To‘lanadigan qarz',
        cashAmount: 'Pul mablag‘i',
        customerAdvances: 'Mijoz avanslari',
        netExposure: 'Sof pozitsiya',
        fxRate: 'Kurs',
        baseCurrency: 'Asosiy valyuta',
        currenciesCount: 'Valyutalar',
        warnings: 'Ogohlantirishlar',
        reconciled: 'Solishtirilgan',
        notReconciled: 'Farqlar',
        paymentPostingDelta: 'Yozuvlar farqi',
        receivablesBalance: 'Qabul qilinadigan qarz qoldig‘i',
        payablesBalance: 'To‘lanadigan qarz qoldig‘i',
        treasuryBalance: 'Kassa va bank qoldig‘i',
        activeAccountedPayments: 'Hisobga olingan to‘lovlar',
        activePaymentPostings: 'To‘lov yozuvlari',
        empty: 'Tanlangan davr uchun ma’lumot yo‘q.',
        loadError: 'Hisobotni yuklab bo‘lmadi.',
        invoiced: 'Hisob-faktura',
        collected: 'Tushum',
        gap: 'Farq',
        rate: 'Tushum darajasi',
        allocatedCollections: 'Taqsimlangan tushumlar',
        unallocatedCollections: 'Taqsimlanmagan tushumlar',
        refunds: 'Qaytarishlar',
        creditNotes: 'Kredit-notalar',
        writeOffs: 'Hisobdan chiqarishlar',
        debitNotes: 'Debet-notalar',
      },
      receivables: {
        title: 'Mijoz hisob-fakturalari',
        detailTitle: 'Hisob-faktura №{id}',
        order: 'Buyurtma',
        shipment: 'Jo‘natma',
        amount: 'Summa',
        paid: 'To‘langan',
        outstanding: 'Qolgan summa',
        created: 'Yaratilgan',
        lines: 'Hisob-faktura qatorlari',
        allocations: 'To‘lov taqsimoti',
        product: 'Mahsulot',
        sku: 'Artikul',
        quantity: 'Miqdor',
        unitPrice: 'Narx',
        lineTotal: 'Qator summasi',
        allocationDate: 'Taqsimlash sanasi',
        allocatedAmount: 'Taqsimlangan',
        noAllocations: 'Bu hisob-faktura bo‘yicha to‘lov taqsimoti yo‘q.',
        empty: 'Hisob-fakturalar topilmadi.',
        loadError: 'Hisob-fakturalarni yuklab bo‘lmadi.',
      },
      payments: {
        newPayment: 'Yangi to‘lov',
        editPayment: 'To‘lovni tahrirlash',
        reverse: 'Bekor qilish',
        reverseConfirm: 'To‘lov bekor qilinsinmi? Kassa yozuvi va hisob-faktura taqsimotlari bekor qilinadi, yozuv o‘chiriladi.',
        reversed: 'To‘lov bekor qilindi.',
        empty: 'To‘lovlar topilmadi.',
        allocationIncomplete: 'Har bir taqsimot uchun hisob-faktura va noldan katta summa kerak.',
        allocationExceeds: 'Taqsimlangan summa to‘lov summasidan oshib ketdi.',
        addAllocation: 'Hisob-faktura qo‘shish',
        allocatedTotal: 'Taqsimlangan',
        unallocated: 'Taqsimlanmagan',
        selectCounterparty: 'Ochiq hisob-fakturalarni yuklash uchun kontragentni tanlang.',
        noOpenInvoices: 'Tanlangan kontragentda ochiq hisob-faktura yo‘q.',
        selectAllocationInvoice: 'Hisob-faktura tanlang',
        recordForInvoice: 'To‘lovni qabul qilish',
        advanceNotice: 'Taqsimlanmagan to‘lov mijoz avansi sifatida hisobga olinadi.',
        loadError: 'To‘lovlarni yuklab bo‘lmadi.',
      },
      adjustments: {
        title: 'Hisob-faktura tuzatishlari',
        newAdjustment: 'Yangi tuzatish',
        adjustmentNumber: 'Tuzatish raqami',
        numberHint: 'Majburiy va takrorlanmas bo‘lishi kerak.',
        reasonHint: 'Tuzatish to‘lovsiz hisob-fakturani to‘g‘rilaydi.',
        refundAccountRequired: 'Qaytarish uchun hisob-faktura valyutasidagi hisobvaraq kerak.',
        refundAccountHint: 'Qaytarish shu hisobvaraqdan o‘tkaziladi.',
        statusHint: 'O‘tkazilgan tuzatishlar hisob-faktura qoldig‘ini darhol o‘zgartiradi.',
        selectInvoice: 'Hisob-faktura tanlang',
        noInvoices: 'Bu kontragentda tuzatish uchun hisob-faktura yo‘q.',
        post: 'O‘tkazish',
        postConfirm: 'Tuzatish o‘tkazilsinmi? Hisob-faktura qoldig‘i va yozuv yangilanadi.',
        posted: 'Tuzatish o‘tkazildi.',
        reverse: 'Bekor qilish',
        reverseConfirm: 'Tuzatish bekor qilinsinmi? Hisob-faktura qoldig‘i va yozuv tiklanadi.',
        reversed: 'Tuzatish bekor qilindi.',
        empty: 'Tuzatishlar topilmadi.',
        loadError: 'Tuzatishlarni yuklab bo‘lmadi.',
      },
      payables: {
        supplierInvoicesTitle: 'Ta’minotchi hisob-fakturalari',
        newInvoice: 'Yangi ta’minotchi hisob-fakturasi',
        editInvoice: 'Ta’minotchi hisob-fakturasini tahrirlash',
        supplier: 'Ta’minotchi',
        invoiceNumber: 'Hisob-faktura raqami',
        invoiceDate: 'Hisob-faktura sanasi',
        numberHint: 'Ta’minotchi uchun takrorlanmas bo‘lishi kerak.',
        purchaseOrder: 'Xarid buyurtmasi',
        noPurchaseOrders: 'Bu ta’minotchi va valyuta uchun mos buyurtma yo‘q.',
        purchaseOrdersUnavailable: 'Xarid buyurtmalarini ko‘rish ruxsatisiz mavjud emas.',
        department: 'Bo‘lim',
        warehouse: 'Ombor',
        openTotal: 'Ochiq summa',
        statusHint: 'To‘lov holati to‘lov taqsimotidan hisoblanadi; faqat bekor qilishni qo‘lda belgilash mumkin.',
        dueBeforeInvoice: 'To‘lov muddati hisob-faktura sanasidan oldin bo‘lishi mumkin emas.',
        void: 'Bekor qilish',
        voidConfirm: 'Ta’minotchi hisob-fakturasi bekor qilinsinmi? U reyestrda qoladi, lekin bekor qilingan hisoblanadi.',
        voided: 'Ta’minotchi hisob-fakturasi bekor qilindi.',
        deleteConfirm: 'Ta’minotchi hisob-fakturasi o‘chirilsinmi? Yozuv reyestrdan olib tashlanadi.',
        noAllocations: 'Bu hisob-faktura bo‘yicha to‘lov taqsimoti yo‘q.',
        empty: 'Ta’minotchi hisob-fakturalari topilmadi.',
        loadError: 'Ta’minotchi hisob-fakturalarini yuklab bo‘lmadi.',
        agingTitle: 'To‘lanadigan qarzlar muddati',
        totalPayables: 'Jami to‘lanadigan qarz',
        overdueDays: 'Muddati o‘tgan kunlar',
        bucket: 'Muddat guruhi',
        agingEmpty: 'Tanlangan filtrlar bo‘yicha to‘lanmagan qarz yo‘q.',
      },
      costing: {
        title: 'Mahsulot tannarxi',
        selectProduct: 'Mahsulot tanlash',
        productPrompt: 'Tannarx hisoblarini yuklash uchun mahsulotni tanlang.',
        productionType: 'Ishlab chiqarish turi',
        productionTypePlaceholder: 'Ishlab chiqarish turi bo‘yicha filtr…',
        version: 'Formula versiyasi',
        calculatedBy: 'Hisoblagan',
        calculatedAt: 'Hisoblangan vaqti',
        baseCost: 'Asosiy tannarx',
        wasteCost: 'Chiqindilar',
        costBeforeProfit: 'Foydadan oldingi tannarx',
        profitCost: 'Foyda',
        finalCost: 'Yakuniy tannarx',
        standardCost: 'Oxirgi tannarx (normativ)',
        variance: 'Oldingisiga nisbatan o‘zgarish',
        noVariance: 'Avvalgi hisob yo‘q',
        components: 'Tannarx tarkibi',
        component: 'Tarkib',
        componentType: 'Turi',
        group: 'Guruh',
        amount: 'Summa',
        quantityPerUnit: 'Bir birlikka miqdor',
        sourceUnitCost: 'Manba narxi',
        noComponents: 'Bu hisobda tarkib yo‘q.',
        marginAnalysis: 'Marja tahlili',
        pricelist: 'Narxlar ro‘yxati',
        noPricelist: 'Narxlar ro‘yxati mavjud emas',
        price: 'Sotish narxi',
        noPrice: 'Bu mahsulot tanlangan narxlar ro‘yxatida yo‘q.',
        margin: 'Marja',
        marginRate: 'Marja darajasi',
        currencyMismatch: 'Narx valyutasi tannarx valyutasidan farq qiladi — marja taqqoslanmaydi.',
        empty: 'Bu mahsulot uchun hali tannarx hisobi yo‘q.',
        loadError: 'Tannarx hisoblarini yuklab bo‘lmadi.',
      },
      productCost: {
        title: 'Mahsulot tannarxi',
        subtitle: 'Versiyalar, manbalar va tasdiqlash',
        backToCosting: 'Tarixiy hisob-kitoblar',
        refreshList: 'Ro‘yxatni yangilash',
        allProducts: 'Barcha mahsulotlar',
        find: 'Qidirish',
        newDraft: 'Yangi qoralama',
        versions: 'Versiyalar',
        noVersions: 'Hozircha versiyalar yo‘q',
        versionHeading: '{product} · versiya {version}',
        batch: 'Partiya',
        expectedOutput: 'Kutilgan chiqish',
        yield: 'Chiqish',
        checkSources: 'Manbalarni tekshirish',
        refreshVersion: 'Versiyani yangilash',
        edit: 'Tahrirlash',
        clone: 'Nusxa yaratish',
        pricingLegend: 'Keyingi hisobdagi taklif narxi',
        pricingMethod: 'Usul',
        pricing: {
          markup: 'Tannarxga ustama %',
          targetMargin: 'Narxdan maqsadli marja %',
          manual: 'Qo‘lda kiritilgan narx',
          markupShort: 'Ustama',
          marginShort: 'Marja',
          manualShort: 'Qo‘lda narx',
        },
        pricingNote: 'Ustama va marja ishlab chiqarish tannarxiga kiritilmaydi.',
        sourceQuality: 'Manbalar sifati',
        calculable: 'Hisoblash mumkin',
        noIssues: 'Manbalar tekshirildi, izohlar yo‘q.',
        blocking: 'Hisoblashni bloklaydi',
        warning: 'Ogohlantirish',
        details: 'Tafsilotlar',
        bomComposition: 'BOM tarkibi',
        bomSummary: '{materials} material · {operations} operatsiya',
        bomNote: 'Hisob natijasidan qat’i nazar BOMning barcha satrlari ko‘rsatiladi. Xarid narxi operator narxidan ustuvor.',
        material: 'Material',
        qtyPerBatch: 'Partiyaga miqdor',
        unit: 'Birlik',
        sourcePrice: 'Manba narxi',
        priceSource: 'Narx manbasi',
        operatorPrice: 'Operator narxi',
        operatorRate: 'Operator stavkasi',
        materialMeta: '{sku} · {sequence}-operatsiya',
        operation: 'Operatsiya',
        durationMinutes: 'Davomiyligi, daq.',
        rate: 'Stavka',
        rateSource: 'Stavka manbasi',
        noBomMaterials: 'BOMda materiallar yo‘q.',
        noBomOperations: 'BOMda operatsiyalar yo‘q.',
        operatorPricesNote: 'O‘zgartirilgan operator narxlari va stavkalari manbalarni tekshirish va hisoblash bilan birga yuboriladi.',
        priceMissing: 'topilmadi — operator narxini kiriting',
        pricePurchaseFromOrders: 'xarid (buyurtmalardan)',
        pricePurchase: 'xarid narxi',
        priceOperator: 'operator narxi',
        purchaseInUse: 'xarid narxi ishlatiladi',
        enterPrice: 'narxni kiriting',
        enterRate: 'stavkani kiriting',
        rateMissing: 'topilmadi',
        elementKnitting: 'Trikotaj materiallari',
        elementOtherMaterial: 'Boshqa materiallar',
        elementPackaging: 'Qadoqlash materiallari',
        elementLabour: 'To‘g‘ridan-to‘g‘ri mehnat',
        elementMachine: 'Mashina',
        elementSubcontracting: 'Subpudrat',
        elementVariableOverhead: 'O‘zgaruvchan qo‘shimcha xarajatlar',
        elementFixedOverhead: 'Doimiy qo‘shimcha xarajatlar',
        elementQuality: 'Sifat va brak',
        elementOtherCost: 'Boshqa to‘g‘ridan-to‘g‘ri xarajatlar',
        priceOverrideIgnored: 'Xarid narxi {price} {currency} ishlatiladi; operator narxi hisobga olinmaydi.',
        priceAppliedDifferent: 'API {typed} o‘rniga {applied} qo‘lladi.',
        serverGroupingNote: 'Material yakunlari API xarajat moddasi bo‘yicha guruhlangan; varaqlar BOM operatsiyasi bo‘yicha guruhlaydi.',
        tabsLabel: 'Tannarx bo‘limlari',
        tab: {
          knitting: 'Trikotaj materiallari',
          materials: 'Boshqa materiallar',
          operations: 'Operatsiyalar',
          expenses: 'Xarajatlar',
          warnings: 'Ogohlantirishlar',
          summary: 'Yakunlar',
          audit: 'Audit',
        },
        lines: {
          item: 'Modda / manba',
          quantity: 'Miqdor',
          sourceRate: 'Manba stavkasi',
          currencyId: 'Valyuta ID',
          fx: 'FX',
          costRate: '{currency} stavkasi',
          amount: 'Summa',
          origin: 'Kelib chiqishi',
        },
        noLines: 'Bu bo‘limda hisoblangan satrlar yo‘q.',
        overridesGap: 'Qo‘lda kiritilgan overrides hozircha API tomonidan qo‘llab-quvvatlanmaydi. Manba ma’lumotlarini tuzatib, qayta hisoblang.',
        serverTotals: 'Server yakunlari · {currency}',
        totals: {
          item: 'Modda',
          perBatch: 'Partiyaga',
          perUnit: 'Birlikka',
        },
        proposedPrices: 'Taklif narxlari',
        prices: {
          method: 'Usul',
          value: 'Qiymat',
          unitPrice: 'Birlik narxi',
        },
        publishGap: 'Tegishli API ulanmaguncha narxlar ro‘yxatiga chop etish mumkin emas.',
        comparableVersion: 'Taqqoslanadigan versiya',
        selectVersion: 'Versiyani tanlang',
        compare: 'Taqqoslash',
        comparisonHint: 'Mahsulotning oxirgi 200 hisob-kitobidagi taqqoslanadigan versiyalar ko‘rsatilgan.',
        unitCostDelta: 'Birlik tannarxining o‘zgarishi (server)',
        comparison: {
          item: 'Modda',
          quantityLeft: 'Chapdagi miqdor',
          quantityRight: 'O‘ngdagi miqdor',
          amountLeft: 'Chapdagi summa',
          amountRight: 'O‘ngdagi summa',
        },
        added: 'Qo‘shilgan',
        removed: 'Olib tashlangan',
        approvalData: 'Tasdiqlash ma’lumotlari',
        approvalGap: 'Versiya maydonlari ko‘rsatilgan. To‘liq o‘tishlar tarixi alohida audit API talab qiladi.',
        auditFields: {
          createdByUserId: 'Yaratgan (foydalanuvchi ID)',
          createdAt: 'Yaratilgan vaqti',
          calculatedByUserId: 'Hisoblagan (foydalanuvchi ID)',
          calculatedAt: 'Hisoblangan vaqti',
          submittedByUserId: 'Yuborgan (foydalanuvchi ID)',
          submittedAt: 'Yuborilgan vaqti',
          approvedByUserId: 'Tasdiqlagan (foydalanuvchi ID)',
          approvedAt: 'Tasdiqlangan vaqti',
          publishedByUserId: 'Chop etgan (foydalanuvchi ID)',
          publishedAt: 'Chop etilgan vaqti',
          rowVersion: 'Satr versiyasi',
          calculationChecksum: 'Hisob nazorat summasi',
        },
        selectVersionPrompt: 'Versiyani tanlang yoki yangi qoralama yarating.',
        publishConfirm: 'Bu versiya chop etilsinmi? Oldingi standart almashtirilishi mumkin.',
        calculateConfirm: 'Qayta hisoblab, saqlangan tannarx satrlari almashtirilsinmi?',
        invalidPricing: 'Manfiy bo‘lmagan qiymat kiriting; maqsadli marja 100% dan kam bo‘lishi kerak.',
        incompatibleVersions: 'Versiyalarning parametrlari yoki valyutalari farq qiladi. Ma’lumotlarni yangilang.',
        status: {
          draft: 'Qoralama',
          calculated: 'Hisoblangan',
          pending_approval: 'Tasdiqlashda',
          approved: 'Tasdiqlangan',
          published: 'Chop etilgan',
          rejected: 'Rad etilgan',
        },
        guidance: {
          bom_missing: 'Mahsulot uchun BOM yarating yoki boshqa versiyani tanlang.',
          material_uom_missing: 'Material uchun bitta faol asosiy o‘lchov birligini sozlang.',
          material_price_missing: 'Tannarx sanasida amal qiladigan xarid narxini kiriting.',
          material_price_stale: 'Xarid narxi dolzarbligini tekshiring va kerak bo‘lsa yangilang.',
          fx_rate_missing: 'Tannarx sanasi uchun tasdiqlangan buxgalteriya kursini qo‘shing.',
          operation_rate_missing: 'Tanlangan smena uchun operatsiya stavkasini kiriting.',
          overhead_rule_conflict: 'Tasdiqlangan qo‘shimcha xarajatlarni taqsimlash qoidalari kesishuvini bartaraf eting.',
        },
        guidanceFallback: 'Hisoblashdan oldin manba ma’lumotlarini tekshiring.',
        action: {
          calculate: 'Hisoblash',
          submit: 'Tasdiqlashga yuborish',
          approve: 'Tasdiqlash',
          reject: 'Rad etish',
          publish: 'Chop etish',
        },
        warningsPrompt: 'Joriy ogohlantirishlarni olish uchun manbalarni tekshirishni ishga tushiring.',
        overheads: {
          title: 'Qo‘shimcha xarajatlar',
          help: 'Tasdiqlangan qoidalar keyingi manba tekshiruvi yoki hisoblashda qo‘llanadi. Qoralamalar saqlanadi, lekin hisobga olinmaydi.',
          add: 'Qo‘shimcha xarajat qo‘shish',
          expense: 'Qo‘shimcha xarajat',
          selectExpense: 'Qo‘shimcha xarajatni tanlang',
          empty: 'Qo‘shimcha xarajat qoidalari topilmadi.',
          noneApplicable: 'Bu versiya uchun mos qoidalar yo‘q.',
          noPermission: 'Qoidalarni ko‘rish uchun cost_overheads.view huquqi kerak.',
          loading: 'Qoidalar yuklanmoqda…',
          code: 'Kod',
          name: 'Nomi',
          basis: 'Taqsimlash bazasi',
          rateType: 'Stavka turi',
          rateValue: 'Stavka',
          percent: 'Foiz',
          amount: 'Summa',
          currency: 'Valyuta',
          scope: 'Qamrov',
          scopeProduct: 'Bu mahsulot',
          scopeAll: 'Barcha mahsulotlar',
          scopeGroup: 'Mahsulot guruhi',
          scopeDepartment: 'Bo‘lim',
          effectiveFrom: 'Amal qilish boshlanishi',
          effectiveTo: 'Amal qilish tugashi',
          priority: 'Ustuvorlik',
          approveNow: 'Darhol tasdiqlash',
          save: 'Xarajatni saqlash',
          cancel: 'Bekor qilish',
          approve: 'Tasdiqlash',
          statusDraft: 'Qoralama',
          statusApproved: 'Tasdiqlangan',
          statusRetired: 'To‘xtatilgan',
          basisDirectMaterialPercent: 'To‘g‘ridan-to‘g‘ri materiallar, %',
          basisDirectLaborPercent: 'To‘g‘ridan-to‘g‘ri mehnat, %',
          basisPrimeCostPercent: 'Ishlab chiqarish tannarxi, %',
          basisPerProducedUnit: 'Ishlab chiqarilgan birlik uchun',
          basisPerMachineHour: 'Mashina-soat uchun',
          basisPerLaborHour: 'Mehnat-soat uchun',
          basisFixedPerBatch: 'Partiya uchun qat’iy',
          invalid: 'Kod, nom, baza, musbat stavka va boshlanish sanasini kiriting.',
          currencyRequired: 'Summali stavka uchun valyutani tanlang.',
        },
        editor: {
          titleCreate: 'Yangi versiya',
          titleEdit: 'Qoralamani tahrirlash',
          titleClone: 'Versiya nusxasi',
          cloneNote: 'Manba taxminlarining yangi qoralamasi yaratiladi. Joriy manbalar va summalarni olish uchun uni hisoblang.',
          permissions: 'Ma’lumotnomalarni tanlash uchun products.view va bom.view huquqlari kerak.',
          retryLoad: 'Yuklashni qayta urinish',
          product: 'Mahsulot',
          selectProduct: 'Mahsulotni tanlang',
          bomVersion: 'BOM versiyasi',
          selectBom: 'BOMni tanlang',
          active: 'faol',
          inactive: 'nofaol',
          currency: 'Valyuta',
          selectCurrency: 'Valyutani tanlang',
          scenarioName: 'Ssenariy nomi',
          productionType: 'Ishlab chiqarish turi',
          costingDate: 'Tannarx sanasi',
          effectiveFrom: 'Amal qilish boshlanishi',
          effectiveTo: 'Amal qilish tugashi',
          batchQuantity: 'Partiya hajmi',
          expectedOutputQuantity: 'Kutilgan chiqish',
          yieldRate: 'Chiqish koeffitsiyenti',
          costType: 'Turi',
          costTypeStandard: 'Standart',
          costTypePlanned: 'Rejali',
          shift: 'Smena',
          shiftDay: 'Kunduzgi',
          shiftNight: 'Tungi',
          shiftBlended: 'Aralash',
          notes: 'Izoh',
          save: 'Saqlash',
          cancel: 'Bekor qilish',
          noBoms: 'Mahsulot uchun BOM versiyalari topilmadi.',
          productBomRequired: 'Mahsulot va uning BOM versiyasini tanlang.',
          currencyRequired: 'Valyutani tanlang.',
          positiveNumbers: 'Miqdor va chiqish musbat sonlar bo‘lishi kerak, nuqtadan keyin 8 xonagacha.',
          yieldMax: 'Chiqish koeffitsiyenti 1 dan oshmasligi kerak.',
          periodInvalid: 'Amal qilish davrini tekshiring.',
        },
      },
      payroll: {
        title: 'Ish haqi bo‘yicha mehnat xarajati',
        gapTitle: 'Hisob-kitoblar ishlab chiqarish va tuzatishlardan shakllanadi',
        gapNotice: 'Ish haqi API davrlar, mehnat hisoblari, tuzatishlar, hisob-kitoblar va o‘tkazmalarni qamrab oladi — ular uchun ish haqi bo‘limidan foydalaning. Bu sahifa xuddi shu davr uchun tabel asosidagi mehnat xarajati hisoboti bo‘lib qoladi.',
        labourCost: 'Mehnat xarajati',
        hours: 'Soatlar',
        cycles: 'Sikllar',
        employees: 'Xodimlar',
        entries: 'Yozuvlar',
        employee: 'Xodim',
        period: 'Davr',
        total: 'Jami',
        empty: 'Tanlangan davr uchun tabel yozuvlari yo‘q.',
        loadError: 'Mehnat xarajati hisobotini yuklab bo‘lmadi.',
      },
      payrollFlow: {
        allPeriods: 'Barcha davrlar',
        selectPeriod: 'Davrni tanlang',
        approve: 'Tasdiqlash',
        reject: 'Rad etish',
        reverse: 'Bekor qilish',
        rejectTitle: 'Yozuvni rad etish',
        reverseTitle: 'Yozuvni bekor qilish',
        rejectMessage: 'Sabab rad etilgan yozuv bilan birga saqlanadi.',
        reverseMessage: 'Bekor qilish sababi audit jurnaliga tushadi.',
        statusOpen: 'Ochiq',
        statusLocked: 'Qulflangan',
        statusClosed: 'Yopilgan',
        employeePickerTitle: 'Xodimni tanlang',
        employeePickerEyebrow: 'Ish haqi',
        employeePickerSearch: 'Xodim',
        employeePickerPlaceholder: 'Ism yoki tabel raqami bo‘yicha qidirish…',
        employeePickerEmpty: 'Ishlab chiqarish xodimlari topilmadi.',
        periods: {
          eyebrow: 'Ish haqi',
          title: 'Ish haqi davrlari',
          description: 'Davrlar hisob-kitob oynasini belgilaydi. Faqat ochiq davrlarni tahrirlash mumkin; qulflash hisoblarni saqlaydi, lekin sanalarni muzlatadi.',
          newPeriod: 'Yangi davr',
          editPeriod: 'Davrni tahrirlash',
          formHint: 'Davrlar kesishmasligi va tugash sanasi boshlanish sanasidan oldin bo‘lmasligi kerak.',
          code: 'Kod',
          name: 'Nomi',
          range: 'Davr',
          lockedAt: 'Qulflangan vaqti',
          lock: 'Qulflash',
          close: 'Yopish',
          lockConfirm: '{code} qulflansinmi? Unga hisoblar hali ham qo‘shilishi mumkin.',
          closeConfirm: '{code} yopilsinmi? Yopish qaytarilmaydi.',
          created: 'Davr yaratildi.',
          updated: 'Davr yangilandi.',
          locked: 'Davr qulflandi.',
          closed: 'Davr yopildi.',
          empty: 'Hali ish haqi davri yo‘q.',
        },
        accruals: {
          eyebrow: 'Ish haqi',
          title: 'Mehnat hisoblari',
          description: 'Ishchi seansi tayyor mahsulot bilan yakunlanganda yaratiladigan ishlab chiqarish daromadi. Tasdiqlangach hisob-kitobda to‘lanadigan bo‘ladi.',
          employee: 'Xodim',
          production: 'Ishlab chiqarish',
          completed: 'Bajarilgan',
          accepted: 'Qabul qilingan',
          rejected: 'Rad etilgan',
          rework: 'Qayta ishlangan',
          rate: 'Narx',
          gross: 'Hisoblangan',
          doneApproved: '{employee} hisobi tasdiqlandi.',
          doneRejected: 'Hisob rad etildi.',
          doneReversed: 'Hisob bekor qilindi.',
          empty: 'Tanlangan filtrlar bo‘yicha hisob yo‘q.',
        },
        accrualStatusProvisional: 'Dastlabki',
        accrualStatusApproved: 'Tasdiqlangan',
        accrualStatusRejected: 'Rad etilgan',
        accrualStatusExported: 'Hisob-kitobda',
        accrualStatusPaid: 'To‘langan',
        accrualStatusReversed: 'Bekor qilingan',
        adjustments: {
          eyebrow: 'Ish haqi',
          title: 'Ish haqi tuzatishlari',
          description: 'Qo‘lda kiritiladigan mukofot, ushlanma, avans, jarima va tuzatishlar. Mukofot va tuzatish daromadga, qolganlari ushlanmaga aylanadi.',
          create: 'Yangi tuzatish',
          employee: 'Xodim',
          selectEmployee: 'Tuzatish uchun xodimni tanlang.',
          period: 'Davr',
          periodRequired: 'Ish haqi davrini tanlang.',
          amountRequired: 'Noldan katta summa kiriting.',
          currencyRequired: 'Valyutani tanlang.',
          typeHint: 'Mukofot va tuzatish hisoblangan summani oshiradi, ushlanma, avans va jarima to‘lanadigan summani kamaytiradi.',
          created: 'Tuzatish yaratildi.',
          doneApproved: 'Tuzatish tasdiqlandi.',
          doneRejected: 'Tuzatish rad etildi.',
          doneReversed: 'Tuzatish bekor qilindi.',
          empty: 'Tanlangan filtrlar bo‘yicha tuzatish yo‘q.',
        },
        adjustmentTypeBonus: 'Mukofot',
        adjustmentTypeDeduction: 'Ushlanma',
        adjustmentTypeAdvance: 'Avans',
        adjustmentTypePenalty: 'Jarima',
        adjustmentTypeCorrection: 'Tuzatish',
        adjustmentStatusDraft: 'Qoralama',
        adjustmentStatusApproved: 'Tasdiqlangan',
        adjustmentStatusRejected: 'Rad etilgan',
        adjustmentStatusUsed: 'Ishlatilgan',
        adjustmentStatusReversed: 'Bekor qilingan',
        runs: {
          eyebrow: 'Ish haqi',
          title: 'Hisob-kitoblar',
          description: 'Har bir davr va valyuta uchun bitta hisob-kitob. U tasdiqlangan hisoblar va tuzatishlarni xodim qatorlariga yig‘adi.',
          generate: 'Hisob-kitob yaratish',
          generateHint: 'Valyutalar hech qachon birlashtirilmaydi — har bir valyuta uchun alohida hisob-kitob yarating.',
          period: 'Davr',
          periodRequired: 'Ish haqi davrini tanlang.',
          currencyRequired: 'Valyutani tanlang.',
          code: 'Kod',
          codeHint: 'Ixtiyoriy — bo‘sh qoldirilsa backend PAYRUN-{period}-{currency} kodini yaratadi.',
          range: 'Davr',
          gross: 'Hisoblangan',
          deductions: 'Ushlanmalar',
          net: 'To‘lanadigan',
          lines: 'Qatorlar',
          empty: 'Hali hisob-kitob yo‘q.',
          generated: '{code} hisob-kitobi yaratildi.',
          generatedPlain: 'Hisob-kitob yaratildi.',
          recalculate: 'Qayta hisoblash',
          approve: 'Hisob-kitobni tasdiqlash',
          post: 'Hisob-kitobni o‘tkazish',
          recalculated: 'Hisob-kitob qayta hisoblandi.',
          approvedRun: 'Hisob-kitob tasdiqlandi.',
          posted: 'Hisob-kitob hisob raqamiga o‘tkazildi.',
          accountRequired: 'Moliyaviy hisobni tanlang.',
          postConfirm: '{code} hisob-kitobi o‘tkazilsinmi? Har bir xodim uchun kassadan chiqim yaratiladi.',
          account: 'Moliyaviy hisob',
          accountHint: 'Faqat tasdiqlangan hisob-kitoblarni o‘tkazish mumkin va hisob valyutasi hisob-kitob valyutasiga mos bo‘lishi kerak.',
          accountCurrencyMismatch: 'Bu hisob {currency} valyutasida emas.',
          detailTitle: 'Hisob-kitob',
          lineTitle: 'Xodim qatorlari',
          noLines: 'Bu hisob-kitobda hali xodim qatorlari yo‘q.',
          employee: 'Xodim',
          department: 'Bo‘lim',
          earnings: 'Daromadlar',
          deductionLines: 'Ushlanmalar',
          quantity: 'Miqdor',
          amount: 'Summa',
        },
        runStatusDraft: 'Qoralama',
        runStatusApproved: 'Tasdiqlangan',
        runStatusPosted: 'O‘tkazilgan',
        runStatusPaid: 'To‘langan',
        runStatusReversed: 'Bekor qilingan',
        postings: {
          eyebrow: 'Ish haqi',
          title: 'Ish haqi o‘tkazmalari',
          description: 'Har bir xodim uchun o‘tkazilgan to‘lov va u bilan bog‘liq kassa operatsiyasi. Bekor qilish pulni qaytaradi.',
          employee: 'Xodim',
          run: 'Hisob-kitob',
          account: 'Hisob',
          net: 'To‘lanadigan',
          postedAt: 'O‘tkazilgan vaqti',
          doneReversed: '{employee} bo‘yicha o‘tkazma bekor qilindi.',
          empty: 'Hali ish haqi o‘tkazmasi yo‘q.',
        },
        postingStatusPosted: 'O‘tkazilgan',
        postingStatusPaid: 'To‘langan',
        postingStatusReversed: 'Bekor qilingan',
        reports: {
          eyebrow: 'Ish haqi',
          title: 'Ish haqi hisobotlari',
          description: 'Tanlangan davr, valyuta va sana oralig‘i uchun faqat o‘qish uchun jamlanmalar va solishtirmalar.',
          runNet: 'To‘lanadigan jami',
          runGross: 'Hisoblangan',
          runDeductions: 'Ushlanmalar',
          runCount: 'Hisob-kitoblar',
          employeeCount: 'Xodimlar',
          section: 'Bo‘lim',
          rows: 'Qatorlar',
          gross: 'Hisoblangan',
          settled: 'Yopilgan',
          open: 'Ochiq',
          runs: 'Hisob-kitoblar',
          accruals: 'Hisoblar',
          postings: 'O‘tkazmalar',
          empty: 'Tanlangan filtrlar bo‘yicha ish haqi ma’lumoti yo‘q.',
          accrualReconciliation: 'Hisoblarni solishtirish',
          accrualReconciliationHint: 'Hisoblar jami ular yaratgan hisob-kitob daromad qatorlari bilan taqqoslanadi.',
          postingReconciliation: 'O‘tkazmalarni solishtirish',
          postingReconciliationHint: 'Hisob-kitob to‘lanadigan jami faol ish haqi o‘tkazmalari bilan taqqoslanadi.',
          reconciled: 'Farq yo‘q.',
          delta: '{amount} farqi tekshirilishi kerak.',
          noRows: 'Bu filtrlar uchun solishtiriladigan ma’lumot yo‘q.',
          accrualGross: 'Hisob bo‘yicha hisoblangan',
          payrollEarnings: 'Hisob-kitob daromadi',
          unexportedApproved: 'Tasdiqlangan, lekin hisob-kitobda emas',
          activePostings: 'Faol o‘tkazmalar',
          reversedPostings: 'Bekor qilingan o‘tkazmalar',
          deltaColumn: 'Farq',
        },
        statement: {
          eyebrow: 'Ish haqi',
          title: 'Xodim hisoboti',
          description: 'Bitta xodim uchun daromadlar, ushlanmalar va o‘tkazmalar. Xodim majburiy — hisobot bo‘sh filtrni qabul qilmaydi.',
          selectEmployee: 'Hisobotni ko‘rish uchun xodimni tanlang.',
          noEarnings: 'Tanlangan oraliqda bu xodim uchun daromad yo‘q.',
          noDeductions: 'Tanlangan oraliqda bu xodim uchun ushlanma yo‘q.',
          noPostings: 'Tanlangan oraliqda bu xodim uchun o‘tkazma yo‘q.',
        },
      },
    },
  },
}
